'use client';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Button, Stack, Typography, Box, List, ListItem, ListItemText, Divider, IconButton, Tooltip, Grid, TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import 'leaflet-control-geocoder';

import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js';

import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

import MapSidebar from './side-map/MapSidebar';
import MapContent from './MapContent';
import TripHistoryList from './content/TripHistoryList';
import PopoverVehicle from './content/PopoverVehicle';
import BottomPopoverTripHistory from './content/BottomPopoverTripHistory';
import VideoStreaming from './content/VideoStreaming';
import { baseMaps } from '../../utils/mapConfig';
import { buildTripTimeline } from '../../utils/mapTripUtils';
import { useLanguage } from '../../utils/i18n';

import PurpleCar from 'assets/images/icons/tracking-truck.webp';
import GreenCar from 'assets/images/icons/moving-truck.webp';
import RedCar from 'assets/images/icons/parking-truck.webp';
import YellowCar from 'assets/images/icons/idle-truck.webp';
import BlackCar from 'assets/images/icons/offline-truck.webp';

import EngineIdleMarker from 'assets/images/icons/engineIdle-marker.webp';
import EngineOffMarker from 'assets/images/icons/engineOff-marker.webp';
import EngineOverMarker from 'assets/images/icons/engineOver-marker.webp';
import MasterPitAddEdit from '../master-data/master-pit/MasterPitAddEdit';
import MasterJettyAddEdit from '../master-data/master-jetty/MasterJettyAddEdit';
import MasterGeofenceLibraryAddEdit from '../master-data/geofence-list/GeofenceLibraryAddEdit';
import { useNotifier } from '../../components/Notifier';
import { useNotificationSettings } from '../../stores/notificationSettingStore';

import { updateGeomPool, deletePool } from '../../stores/poolStore';
import { updateGeomTransitPoint, deleteTransitPoint } from '../../stores/transitPointStore';
import { updateGeomGeofence, deleteGeofence } from '../../stores/geofenceStore';

import LayersIcon from '@mui/icons-material/Layers';
import SpeedIcon from '@mui/icons-material/Speed';

const DEFAULT_ICON = BlackCar;

import dayjs from 'dayjs';

const getVehicleIcon = (vehicle) => {
  if (!vehicle) return BlackCar;
  const status = String(vehicle.status || vehicle.type || '').toLowerCase();
  const gpsStatus = String(vehicle.gps || '').toLowerCase();
  const engine = String(vehicle.engine || vehicle.engine_status || '').toUpperCase();
  const speed = Number(vehicle.speed) || 0;

  const spatialId = Number(vehicle.raw?.spatial_id || vehicle.spatial_id || vehicle.info?.spatial_id || 0);
  const spatialMaxSpeed = Number(vehicle.raw?.spatial_max_speed || vehicle.spatial_max_speed || vehicle.info?.spatial_max_speed || 0);
  const isOverSpeedSpatial = spatialId > 0 && spatialMaxSpeed > 0 && speed > spatialMaxSpeed;
  const isStatusOverSpeed = status.includes('over speed');
  const isGpsUpdated = gpsStatus === 'update' || gpsStatus === 'updated';

  const isAtPolygon = status === 'at pool' || status === 'at geofence' || status === 'at customer';
  const isAtTrip = status === 'at trip';

  if (status.includes('delay') || !isGpsUpdated) return RedCar;

  if (isAtTrip && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (isAtPolygon && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (isOverSpeedSpatial && isStatusOverSpeed && engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;

  if ((isAtTrip || isAtPolygon) && engine === 'ON' && speed === 0 && isGpsUpdated) return YellowCar;
  if ((isAtTrip || isAtPolygon) && engine === 'IDLE' && speed === 0 && isGpsUpdated) return YellowCar;

  if (isAtTrip && engine === 'OFF') return BlackCar;
  if (isAtPolygon && speed === 0) return PurpleCar;

  if (engine === 'OFF' || status.includes('offline')) return BlackCar;

  if (engine === 'ON' && speed > 0 && isGpsUpdated) return GreenCar;
  if (engine === 'ON' && speed === 0 && isGpsUpdated) return YellowCar;
  if (engine === 'IDLE' && speed === 0 && isGpsUpdated) return YellowCar;

  return DEFAULT_ICON;
};

const formatVehicleDataForPopover = (v, notifSettings = {}) => {
  if (!v) return null;

  const activeRitaseStr = localStorage.getItem('active_ritase_data');
  let activeRitaseMap = {};
  try {
    if (activeRitaseStr) {
      const parsed = JSON.parse(activeRitaseStr);
      activeRitaseMap = parsed.data || {};
    }
  } catch (e) {
    console.warn('Failed to parse active_ritase_data', e);
  }

  const vId = v.raw?.vehicle_id || v.vehicle_id || v.id || v.vehicle;

  const rawTime = v.last_update || v.info?.modified_date;
  let ritaseEnd = 0;
  const checkTime = rawTime ? dayjs(rawTime).valueOf() : dayjs().valueOf();

  const trackingStateStr = localStorage.getItem('vehicle_tracking_state');
  let trackingState = {};
  try {
    if (trackingStateStr) trackingState = JSON.parse(trackingStateStr);
  } catch {}
  const vState = trackingState[vId] || {};

  let isLoadedRitase = false;
  if (activeRitaseMap && activeRitaseMap[vId]) {
    for (const trip of activeRitaseMap[vId]) {
      const start = trip.start ? dayjs(trip.start).valueOf() : 0;
      const pureEnd = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).valueOf() : Infinity;
      const endGrace = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).add(30, 'minute').valueOf() : Infinity;

      if (start > 0 && checkTime >= start && checkTime <= endGrace) {
        if (checkTime <= pureEnd) {
          isLoadedRitase = true;
        }
        if (pureEnd !== Infinity && pureEnd > ritaseEnd) ritaseEnd = pureEnd;
      }
    }
  }

  let isLoadedCheckpoint = false;
  if (vState.checkpoint_status && vState.checkpoint_time >= ritaseEnd) {
    isLoadedCheckpoint = vState.checkpoint_status === 'LOADED';
  }

  let isLoaded = false;
  if (vState && typeof vState.is_loaded === 'boolean') {
    isLoaded = vState.is_loaded;
  } else {
    isLoaded = isLoadedRitase || isLoadedCheckpoint || (v.is_in_retase !== false && !trackingStateStr);
  }

  const result = {
    id: v.vehicle ?? v.info?.id ?? v.id ?? `${v.vehicle_license_plate ?? 'unknown'}-${v.imei_no ?? ''}`,
    fleet_name: v.vehicle_license_plate || v.info?.name || 'Unknown',
    status: v.status || v.info?.status_text || 'unknown',
    vehicle_type: v.vehicle_type_name || v.info?.vehicle_type_name || v.vehicle_type || '-',
    driver_name: v.driver_name || v.join?.driver_name || v.info?.driver_name || '-',
    imei: v.imei_no || v.integration_imei || v.imei || '-',
    integration_imei: v.raw?.raw?.integration_imei || v.raw?.integration_imei || v.info?.integration_imei,
    vehicle_odometer_km: v.current_odo ?? v.info?.current_odo ?? 0,
    last_location_time: v.last_update || v.info?.modified_date || '-',
    last_connected_time: v.last_update || '-',
    hull_no: v.vehicle_name || '-',
    stop_duration: v.stop_duration || '-',
    engine_status: v.engine ?? v.info?.engine_status ?? v.engine_status ?? '-',
    distance_traveled_km: v.info?.total_distance ?? 0,
    trip_time: v.trip_time ?? '-',
    fuel_liter:
      typeof v.fuel === 'number'
        ? v.fuel
        : v.canbus?.canFuelPercentage || v.info?.canFuelPercentage || v.fuel || v.info?.fuel_capacity || '-',
    total_work_hours: v.total_work_hours ?? v.info?.canEngineWorktime ?? '-',
    last_location: {
      address: v.street || v.location || `${v.sub_district || ''} ${v.district || ''}`.trim() || '-',
      is_location_updated: (v.gps || '').toString().toLowerCase() === 'update'
    },
    raw: v,
    isLoaded: isLoaded
  };

  const spatialId = Number(v.raw?.spatial_id || v.spatial_id || v.info?.spatial_id || 0);
  const spatialMaxSpeed = Number(v.raw?.spatial_max_speed || v.spatial_max_speed || v.info?.spatial_max_speed || 0);
  const spatialMinSpeed = Number(v.raw?.spatial_min_speed || v.spatial_min_speed || v.info?.spatial_min_speed || 0);
  const speed = Number(v.raw?.speed || v.speed || v.info?.speed || 0);
  const polygonTypeName =
    v.raw?.polygon_type_name || v.polygon_type_name || v.info?.polygon_type_name || v.raw?.spatial_description || 'Geofence';

  const engineStatus = String(v.engine || v.engine_status || v.info?.engine_status || '').toUpperCase();
  const status = String(v.status || v.info?.status_text || '').toLowerCase();

  const isAtPolygon =
    status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
  const isAtTrip = status === 'at trip';

  const speedMsgCheck = String(v.raw?.speed_message || v.speed_message || '').toLowerCase();
  const limitSpeedCheck = Number(v.vehicle?.max_speed || 70);
  const isOverSpeedCondition = speedMsgCheck.includes('over') || (engineStatus === 'ON' && speed >= limitSpeedCheck);

  const isMoving = engineStatus === 'ON' && speed > 0 && (isAtTrip || isAtPolygon || isOverSpeedCondition) && !status.includes('delay');

  if (isMoving) {
    const rawData = v.raw || v;
    const eventName = String(rawData.event || rawData.alarm_type || '').toLowerCase();
    const harshEventStr = String(v.raw?.harsh_event || v.harsh_event || v.info?.harsh_event || '').toLowerCase();

    const isHarshBraking = harshEventStr.includes('braking') || harshEventStr.includes('breaking') || eventName.includes('harsh braking');
    const isHarshAccel = harshEventStr.includes('acceleration') || eventName.includes('harsh acceleration');
    const isHarshCorner = harshEventStr.includes('cornering') || eventName.includes('harsh cornering');
    const isPanic = eventName.includes('panic') || eventName.includes('sos');

    if (isPanic && notifSettings.panicButton) {
      result.polygonSpeedAlertText = '🚨 Darurat! Panic Button Ditekan!';
      result.pulseReason = '🚨 Darurat! Panic Button Ditekan!';
      result.pulseColor = 'error';
    } else if (isHarshBraking && notifSettings.harshBraking) {
      result.polygonSpeedAlertText = `Harsh Braking`;
      result.pulseReason = `Harsh Braking`;
      result.pulseColor = 'error';
    } else if (isHarshAccel && notifSettings.harshAcceleration) {
      result.polygonSpeedAlertText = `Harsh Acceleration`;
      result.pulseReason = `Harsh Acceleration`;
      result.pulseColor = 'error';
    } else if (isHarshCorner && notifSettings.harshCornering) {
      result.polygonSpeedAlertText = `Harsh Cornering`;
      result.pulseReason = `Harsh Cornering`;
      result.pulseColor = 'error';
    } else if (isLoaded && spatialId > 0 && spatialMaxSpeed > 0 && speed > spatialMaxSpeed) {
      result.polygonSpeedAlertText = `${result.fleet_name} Over speed ${speed} in location ${polygonTypeName}, Spatial Max speed : ${spatialMaxSpeed}`;
      result.pulseReason = `${result.fleet_name} Over speed ${speed} in location ${polygonTypeName}, Spatial Max speed : ${spatialMaxSpeed}`;
      result.pulseColor = 'error';
    } else if (isLoaded && spatialId > 0 && spatialMinSpeed > 0 && speed > 0 && speed < spatialMinSpeed) {
      result.polygonSpeedAlertText = `${result.fleet_name} Under speed ${speed} in location ${polygonTypeName}, Spatial Min speed : ${spatialMinSpeed}`;
      result.pulseReason = `${result.fleet_name} Under speed ${speed} in location ${polygonTypeName}, Spatial Min speed : ${spatialMinSpeed}`;
      result.pulseColor = 'warning';
    } else {
      const speedMsg = String(v.raw?.speed_message || v.speed_message || '').toLowerCase();
      const limitSpeed = Number(v.vehicle?.max_speed || 70);

      if (speedMsg.includes('over') && notifSettings.overSpeed) {
        result.polygonSpeedAlertText = null;
        result.pulseReason = `Overspeed Warning`;
        result.pulseColor = 'error';
      } else if (speedMsg.includes('under')) {
        result.polygonSpeedAlertText = null;
        result.pulseReason = `Underspeed Warning`;
        result.pulseColor = 'warning';
      } else if (engineStatus === 'ON' && speed >= limitSpeed && notifSettings.overSpeed) {
        result.polygonSpeedAlertText = null;
        result.pulseReason = `Overspeed (> ${limitSpeed} km/h)`;
        result.pulseColor = 'error';
      } else if (engineStatus === 'IDLE') {
        result.polygonSpeedAlertText = null;
        result.pulseReason = `Engine IDLE`;
        result.pulseColor = 'warning';
      } else {
        result.polygonSpeedAlertText = null;
        result.pulseReason = null;
      }
    }
  }

  return result;
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calculateHeading = (lat1, lon1, lat2, lon2) => {
  const toRad = (degree) => (degree * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const φ1 = toRad(lat1),
    φ2 = toRad(lat2),
    Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const isPointInPolygon = (latlng, poly) => {
  if (!poly || typeof poly.getLatLngs !== 'function') return false;
  const lat = latlng[0];
  const lng = latlng[1];
  const coords = poly.getLatLngs()[0];

  let isInside = false;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lat,
      yi = coords[i].lng;
    const xj = coords[j].lat,
      yj = coords[j].lng;
    const intersect = yi > lng !== yj > lng && lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

export default function Map() {
  const { settings: notifSettings } = useNotificationSettings();
  const { t } = useLanguage();
  const lastIndexRef = useRef(-1);
  const wrapperRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const [videoTarget, setVideoTarget] = useState(null);

  useEffect(() => {
    let styleTag = document.getElementById('glow-blink-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'glow-blink-style';
      styleTag.innerHTML = `
        @keyframes pulseRingRed {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes pulseRingYellow {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-ring {
          position: absolute;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          z-index: 1;
          display: none;
        }
        .pulse-ring.glow-overspeed {
          display: block;
          background-color: rgba(244, 67, 54, 0.6);
          box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
          animation: pulseRingRed 1s infinite ease-out;
        }
        @keyframes pulseRingOrange {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-ring.glow-overspeed-spatial {
          display: block;
          background-color: rgba(243, 147, 153, 0.6);
          box-shadow: 0 0 10px rgba(243, 147, 153, 0.8);
          animation: pulseRingOrange 1s infinite ease-out;
        }
        .pulse-ring.glow-idle {
          display: block;
          background-color: rgba(255, 193, 7, 0.6);
          box-shadow: 0 0 10px rgba(255, 193, 7, 0.8);
          animation: pulseRingYellow 1.5s infinite ease-out;
        }
        .polygon-glow-active {
          stroke: var(--glow-color, #ffffff) !important;
          stroke-width: 4px !important;
          stroke-dasharray: 6 !important;
          animation: polygon-march 1s linear infinite;
          filter: drop-shadow(0px 0px 4px var(--glow-color, #ffffff)) brightness(1.5) !important;
        }
        @keyframes polygon-march {
          to { stroke-dashoffset: -12; }
        }
      `;
      document.head.appendChild(styleTag);
    }
  }, []);

  const updateVehicleRotation = useCallback((currentIndex, pathArray, providedAngle = null, currentDataWp = null) => {
    if (!tripMarkerRef.current || !pathArray || pathArray.length === 0) return;

    const vehicleImg = document.getElementById('trip-vehicle-img');
    if (!vehicleImg) return;

    let glowClass = '';
    if (currentDataWp) {
      const spatialId = Number(currentDataWp.spatial_id || currentDataWp.info?.spatial_id || 0);
      const spatialMaxSpeed = Number(currentDataWp.spatial_max_speed || currentDataWp.info?.spatial_max_speed || 0);
      const spatialMinSpeed = Number(currentDataWp.spatial_min_speed || currentDataWp.info?.spatial_min_speed || 0);
      const engineStatus = String(
        currentDataWp.engine || currentDataWp.engine_status || currentDataWp.info?.engine_status || ''
      ).toUpperCase();
      const currentSpeed = Number(currentDataWp.speed || 0);
      const limitSpeed = Number(currentDataWp.vehicle?.max_speed || 70);

      const status = String(currentDataWp.status || currentDataWp.info?.status_text || currentDataWp.type || '').toLowerCase();
      const isAtPolygon =
        status.includes('at pool') || status.includes('at geofence') || status.includes('at customer') || status.includes('at polygon');
      const isAtTrip = status === 'at trip';

      const vId = currentDataWp.raw?.vehicle_id || currentDataWp.vehicle_id || currentDataWp.id || currentDataWp.vehicle;
      const rawTime = currentDataWp.last_update || currentDataWp.info?.modified_date;
      const checkTime = rawTime ? dayjs(rawTime).valueOf() : dayjs().valueOf();

      const activeRitaseStr = localStorage.getItem('active_ritase_data');
      let activeRitaseMap = {};
      try {
        if (activeRitaseStr) activeRitaseMap = JSON.parse(activeRitaseStr).data || {};
      } catch (e) {
        console.log('Error', e);
      }

      let isLoadedRitase = false;
      let ritaseEnd = 0;
      if (vId && activeRitaseMap[vId]) {
        for (const trip of activeRitaseMap[vId]) {
          const start = trip.start ? dayjs(trip.start).valueOf() : 0;
          const pureEnd = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).valueOf() : Infinity;
          const endGrace = trip.end && trip.end !== 'no-end' ? dayjs(trip.end).add(30, 'minute').valueOf() : Infinity;
          if (start > 0 && checkTime >= start && checkTime <= endGrace) {
            if (checkTime <= pureEnd) isLoadedRitase = true;
            if (pureEnd !== Infinity && pureEnd > ritaseEnd) ritaseEnd = pureEnd;
          }
        }
      }

      const trackingStateStr = localStorage.getItem('vehicle_tracking_state');
      let trackingState = {};
      try {
        if (trackingStateStr) trackingState = JSON.parse(trackingStateStr);
      } catch {}
      const vState = trackingState[vId] || {};

      let isLoadedCheckpoint = false;
      if (vState.checkpoint_status && vState.checkpoint_time >= ritaseEnd) {
        isLoadedCheckpoint = vState.checkpoint_status === 'LOADED';
      }

      let isLoaded = false;
      if (vState && typeof vState.is_loaded === 'boolean') {
        isLoaded = vState.is_loaded;
      } else {
        isLoaded = isLoadedRitase || isLoadedCheckpoint || (currentDataWp.is_in_retase !== false && !trackingStateStr);
      }

      const isStatusOverSpeed = status.includes('over speed') || status.includes('overspeed');
      const isStatusUnderSpeed = status.includes('under speed');
      const isOverSpeedSpatial = isLoaded && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed;
      const isUnderSpeedSpatial = spatialId > 0 && spatialMinSpeed > 0 && currentSpeed > 0 && currentSpeed < spatialMinSpeed;
      const isGpsUpdated = (currentDataWp.gps || '').toString().toLowerCase() === 'update';

      const speedMsgStr = String(currentDataWp.speed_message || '').toLowerCase();
      const isOverSpeedMsg = speedMsgStr.includes('over');
      const isOverSpeedLimit = engineStatus === 'ON' && currentSpeed >= limitSpeed;

      const isMoving =
        engineStatus === 'ON' &&
        currentSpeed > 0 &&
        (isAtTrip ||
          isAtPolygon ||
          isOverSpeedSpatial ||
          isStatusOverSpeed ||
          isUnderSpeedSpatial ||
          isStatusUnderSpeed ||
          isOverSpeedMsg ||
          isOverSpeedLimit) &&
        isGpsUpdated &&
        !status.includes('delay');

      if (isMoving) {
        const harshEventStr = String(currentDataWp.harsh_event || currentDataWp.info?.harsh_event || '').toLowerCase();
        const eventNameStr = String(currentDataWp.event || currentDataWp.alarm_type || '').toLowerCase();
        const isHarsh =
          harshEventStr.includes('braking') ||
          harshEventStr.includes('breaking') ||
          harshEventStr.includes('acceleration') ||
          harshEventStr.includes('cornering') ||
          eventNameStr.includes('harsh') ||
          eventNameStr.includes('panic') ||
          eventNameStr.includes('sos');

        if (isHarsh) {
          glowClass = 'glow-overspeed';
        } else if (isOverSpeedSpatial) {
          glowClass = 'glow-overspeed-spatial';
        } else if (isUnderSpeedSpatial) {
          glowClass = 'glow-idle';
        } else if (engineStatus === 'ON' && (currentSpeed >= limitSpeed || isStatusOverSpeed)) {
          glowClass = 'glow-overspeed';
        } else if (engineStatus === 'IDLE') {
          glowClass = 'glow-idle';
        }
      }
    }

    const pulseRing = document.getElementById('trip-vehicle-pulse');
    if (pulseRing && pulseRing.className !== `pulse-ring ${glowClass}`) {
      pulseRing.className = `pulse-ring ${glowClass}`;
    }

    // 1. Menggunakan field angle/direction dari API jika tersedia (Lebih Akurat)
    if (providedAngle !== null && providedAngle !== undefined && providedAngle !== '') {
      vehicleImg.style.transform = `rotate(${Number(providedAngle) - 90}deg)`;
      return;
    }

    // 2. Fallback Kalkulasi Manual berdasarkan koordinat jika angle API kosong
    const currentLoc = pathArray[Math.floor(currentIndex)];
    const isAtEnd = Math.floor(currentIndex) >= pathArray.length - 1;

    let refLoc1 = currentLoc;
    let refLoc2 = null;

    if (!isAtEnd) {
      refLoc2 = pathArray[Math.floor(currentIndex) + 1];
    } else {
      refLoc1 = pathArray[Math.floor(currentIndex) - 1] || currentLoc;
      refLoc2 = currentLoc;
    }

    if (refLoc1 && refLoc2 && (refLoc1[0] !== refLoc2[0] || refLoc1[1] !== refLoc2[1])) {
      let heading = calculateHeading(refLoc1[0], refLoc1[1], refLoc2[0], refLoc2[1]);
      vehicleImg.style.transform = `rotate(${heading - 90}deg)`;
    }
  }, []);

  const markerClusterRef = useRef(null);
  const locationLayerRef = useRef(null);

  const tripLayerRef = useRef(null);
  const tripMarkerRef = useRef(null);
  const animRef = useRef(null);
  const animIndexRef = useRef(0);
  // const lastSpeedUpdateTimeRef = useRef(0);

  const notify = useNotifier();

  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  // const layerControlRef = useRef(null);
  const eventMarkersRef = useRef({});
  const popupTimeoutRef = useRef(null);
  const currentAreaIdRef = useRef(null);
  const lastEventPopupTimeRef = useRef(0);

  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);

  const vehicleMarkersRef = useRef({});
  const isRecreatingRef = useRef(false);

  const getVehicleUniqueId = (v) => {
    return v?.id || v?.vehicle_id || v?.vehicle || v?.vehicle_license_plate || null;
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [mapData, setMapData] = useState({ vehicles: [], locations: [] });

  const [drawnLayer, setDrawnLayer] = useState(null);
  const [drawingPopoverOpen, setDrawingPopoverOpen] = useState(false);
  const [drawingPopoverAnchor, setDrawingPopoverAnchor] = useState(null);
  const [polygonWkt, setPolygonWkt] = useState('');

  const [activeLocationPolygon, setActiveLocationPolygon] = useState(null);

  const [locPopoverOpen, setLocPopoverOpen] = useState(false);
  // const [locPopoverAnchor, setLocPopoverAnchor] = useState(null);
  useEffect(() => {
    if (locPopoverOpen && activeLocationPolygon?.layer?._path) {
      const color = activeLocationPolygon.layer.options?.color || '#ffffff';
      activeLocationPolygon.layer._path.style.setProperty('--glow-color', color);
      activeLocationPolygon.layer._path.classList.add('polygon-glow-active');
    }
    return () => {
      if (activeLocationPolygon?.layer?._path) {
        activeLocationPolygon.layer._path.classList.remove('polygon-glow-active');
        activeLocationPolygon.layer._path.style.removeProperty('--glow-color');
      }
    };
  }, [activeLocationPolygon, locPopoverOpen]);

  const [isEditingPolygon, setIsEditingPolygon] = useState(false);
  const [editedPolygonName, setEditedPolygonName] = useState('');
  const [editedWkt, setEditedWkt] = useState('');
  const [showWktInput, setShowWktInput] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [pitDialogOpen, setPitDialogOpen] = useState(false);
  const [jettyDialogOpen, setJettyDialogOpen] = useState(false);
  const [geoDialogOpen, setGeoDialogOpen] = useState(false);

  const [circlePopoverOpen, setCirclePopoverOpen] = useState(false);
  const [circlePopoverAnchor, setCirclePopoverAnchor] = useState(null);
  const [circleInfo, setCircleInfo] = useState({ radius: 0, vehicles: [] });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);

  const selectedVehicleIdRef = useRef(selectedVehicleId);
  useEffect(() => {
    selectedVehicleIdRef.current = selectedVehicleId;
  }, [selectedVehicleId]);

  const [isTripHistoryOpen, setIsTripHistoryOpen] = useState(false);
  const [tripVehicleTarget, setTripVehicleTarget] = useState(null);
  const [tripWaypoints, setTripWaypoints] = useState({ all: [], normal: [], off: [], drb: [] });
  const [isWpLoading, setIsWpLoading] = useState(false);
  const [wpLoadingProgress, setWpLoadingProgress] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);

  const [playbackSpeed, setPlaybackSpeed] = useState(0.5);
  const playbackSpeedRef = useRef(0.5);

  const [currentPointSpeed, setCurrentPointSpeed] = useState(0);
  const [currentRPM, setCurrentRPM] = useState(0);
  const [currentFuel, setCurrentFuel] = useState({ litre: 0, percent: 0, cap: 100 });
  const [currentIndexState, setCurrentIndexState] = useState(0);

  const currentPointSpeedRef = useRef(currentPointSpeed);

  const [followedVehicleId, setFollowedVehicleId] = useState(null);
  const lastFollowedVehicleIdRef = useRef(null);

  const handleFollowVehicle = useCallback((vehicleId) => {
    setFollowedVehicleId((prev) => (prev === vehicleId ? null : vehicleId));
  }, []);
  const flyToWithOffset = useCallback(
    (lat, lon, zoomLevel, yOffset = 0) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const currentZoom = map.getZoom();
      const targetZoom = zoomLevel ?? currentZoom;
      const sidebarWidth = isSidebarOpen ? 340 : 0;
      const targetLatLng = L.latLng(lat, lon);
      const targetPoint = map.project(targetLatLng, targetZoom);

      targetPoint.x += sidebarWidth / 2;
      targetPoint.y -= yOffset;
      if (isTripHistoryOpen) {
        targetPoint.y += 100;
      }

      const offsetLatLng = map.unproject(targetPoint, targetZoom);
      if (currentZoom === targetZoom) {
        map.panTo(offsetLatLng, { animate: true, duration: 0.5 });
      } else {
        map.flyTo(offsetLatLng, targetZoom, { animate: true, duration: 0.5 });
      }
    },
    [isSidebarOpen, isTripHistoryOpen]
  );

  useEffect(() => {
    if (followedVehicleId && mapData.vehicles && mapData.vehicles.length > 0) {
      const v = mapData.vehicles.find((vh) => {
        const id = vh.vehicle ?? vh.info?.id ?? vh.id ?? `${vh.vehicle_license_plate ?? 'unknown'}-${vh.imei_no ?? ''}`;
        return id === followedVehicleId;
      });
      if (v) {
        const lat = v.lat ?? v.latitude ?? v.centroid_lat ?? v.info?.centroid_lat;
        const lon = v.lon ?? v.lng ?? v.longitude ?? v.centroid_lon ?? v.info?.centroid_lon;
        if (lat != null && lon != null) {
          const map = mapInstanceRef.current;
          if (!map) return;

          if (lastFollowedVehicleIdRef.current !== followedVehicleId) {
            lastFollowedVehicleIdRef.current = followedVehicleId;
            flyToWithOffset(Number(lat), Number(lon), 20);
          } else {
            const zoomLevel = map.getZoom();
            const sidebarWidth = isSidebarOpen ? 340 : 0;
            const targetLatLng = L.latLng(Number(lat), Number(lon));
            const targetPoint = map.project(targetLatLng, zoomLevel);

            targetPoint.x += sidebarWidth / 2;
            if (isTripHistoryOpen) {
              targetPoint.y += 100;
            }

            const offsetLatLng = map.unproject(targetPoint, zoomLevel);
            map.panTo(offsetLatLng, { animate: true, duration: 0.5 });
          }
        }
      }
    } else {
      if (!followedVehicleId) {
        lastFollowedVehicleIdRef.current = null;
      }
    }
  }, [mapData.vehicles, followedVehicleId, flyToWithOffset, isSidebarOpen, isTripHistoryOpen]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      if (wrapperRef.current?.requestFullscreen) {
        wrapperRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
      }, 1500);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle container resize dynamically (untuk sidebars buka/tutup, zoom in/out, window resize)
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const handleResize = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(mapContainerRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    currentPointSpeedRef.current = currentPointSpeed;
  }, [currentPointSpeed]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  const updatePlaying = useCallback((next) => {
    isPlayingRef.current = next;
    setIsPlaying(next);
  }, []);

  const handleChangePlaybackSpeed = useCallback((newSpeed) => {
    setPlaybackSpeed(newSpeed);
  }, []);

  const {
    path: derivedPath,
    speeds: derivedSpeeds,
    allWaypoints
  } = useMemo(() => {
    const wpsAll = tripWaypoints.all || [];
    // Single source of truth: record diurutkan ascending by start_wp_time dan difilter koordinat valid SEKALI,
    // lalu path[i], speeds[i] dan allWaypoints[i] diambil dari record yang sama (index 1:1).
    const timeline = buildTripTimeline(wpsAll);
    return {
      allWaypoints: timeline.waypoints,
      path: timeline.path,
      speeds: timeline.speeds
    };
  }, [tripWaypoints.all]);
  const derivedPathRef = useRef(derivedPath);
  const derivedSpeedsRef = useRef(derivedSpeeds);
  const allWaypointsRef = useRef(allWaypoints);

  useEffect(() => {
    derivedPathRef.current = derivedPath;
    derivedSpeedsRef.current = derivedSpeeds;
    allWaypointsRef.current = allWaypoints;
  }, [derivedPath, derivedSpeeds, allWaypoints]);

  const maxIndex = useMemo(() => Math.max(0, derivedPath.length - 1), [derivedPath.length]);

  const currentPercent = useMemo(() => {
    if (maxIndex === 0) return 0;
    return (currentIndexState / maxIndex) * 100;
  }, [currentIndexState, maxIndex]);

  const panToWithOffset = useCallback(
    (lat, lon, animate = true) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      const zoomLevel = map.getZoom();
      const sidebarWidth = isSidebarOpen ? 340 : 0;

      const targetLatLng = L.latLng(lat, lon);
      const targetPoint = map.project(targetLatLng, zoomLevel);

      targetPoint.x += sidebarWidth / 2;
      targetPoint.y += 50;
      const offsetLatLng = map.unproject(targetPoint, zoomLevel);

      map.panTo(offsetLatLng, { animate, duration: 0.1, easeLinearity: 0.5 });
    },
    [isSidebarOpen]
  );

  const getPolygonWKT = (layer) => {
    if (layer instanceof L.Polygon) {
      const latlngs = layer.getLatLngs()[0];
      if (!latlngs || latlngs.length === 0) return '';
      const points = [...latlngs, latlngs[0]].map((ll) => `${ll.lng} ${ll.lat}`).join(', ');
      return `POLYGON((${points}))`;
    }
    return '';
  };

  useEffect(() => {
    if (mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [-2.5489, 118.0148],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
      fullscreenControl: false,
      layers: [baseMaps['Satelite Map'] || Object.values(baseMaps)[0]]
    });

    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: true,
      position: 'topleft',
      placeholder: 'Search area...'
    }).addTo(map);

    map.pm.addControls({
      position: 'topleft',
      drawPolyline: true,
      drawPolygon: true,
      drawCircle: true,
      removalMode: true,

      drawMarker: false,
      drawCircleMarker: false,
      drawRectangle: false,
      editMode: false,
      dragMode: false,
      cutPolygon: false,
      drawText: false,
      rotateMode: false,

      oneBlock: true
    });

    map.pm.setPathOptions({
      color: '#485D9A',
      fillColor: '#485D9A',
      fillOpacity: 0.4
    });

    let tempDistanceMarkers = [];
    let totalDistanceKm = 0;

    map.on('pm:drawstart', ({ workingLayer, shape }) => {
      if (shape === 'Circle') {
        workingLayer.setStyle({ color: '#2196f3', fillColor: '#2196f3', fillOpacity: 0.2 });
      }

      if (shape !== 'Line' && shape !== 'Polyline') return;

      totalDistanceKm = 0;
      tempDistanceMarkers = [];

      workingLayer.on('pm:vertexadded', (e) => {
        const latlngs = workingLayer.getLatLngs();

        if (latlngs.length > 1) {
          const prevPoint = latlngs[latlngs.length - 2];
          const currPoint = latlngs[latlngs.length - 1];

          const dist = calculateDistance(prevPoint.lat, prevPoint.lng, currPoint.lat, currPoint.lng);
          totalDistanceKm += dist;

          const distMarker = L.marker(currPoint, {
            icon: L.divIcon({
              className: 'custom-measure-tooltip',
              html: `<div style="
                        background: rgba(0, 0, 0, 0.7); 
                        color: #ffffff; 
                        font-size: 11px; 
                        font-weight: bold; 
                        padding: 4px 6px; 
                        border-radius: 4px;
                        white-space: nowrap;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                        display: inline-block;
                      ">${totalDistanceKm.toFixed(2)} km</div>`,
              iconSize: [0, 0]
            }),
            interactive: false
          }).addTo(map);

          tempDistanceMarkers.push(distMarker);
        }
      });
    });

    const makeNewLayerEditable = (layer, shape) => {
      layer.on('click', (evt) => {
        L.DomEvent.stopPropagation(evt);

        map.eachLayer((l) => {
          if (l.pm && l !== layer) l.pm.disable();
        });

        layer.pm.enable({ allowSelfIntersection: false });

        const centerLatLng = layer.getBounds ? layer.getBounds().getCenter() : layer.getLatLng();
        const containerPoint = map.latLngToContainerPoint(centerLatLng);

        if (shape === 'Circle') {
          setCirclePopoverAnchor({ top: containerPoint.y, left: containerPoint.x });
          setCircleInfo({ radius: layer.getRadius(), centerLoc: layer.getLatLng() });
          setCirclePopoverOpen(true);
        } else {
          setDrawingPopoverAnchor({ top: containerPoint.y, left: containerPoint.x });
          setDrawingPopoverOpen(true);
          setDrawnLayer({ layer, shape });

          if (shape === 'Polygon') setPolygonWkt(getPolygonWKT(layer));
        }
      });
      layer.on('pm:edit', () => {
        if (shape === 'Polygon') {
          setPolygonWkt(getPolygonWKT(layer));
        } else if (shape === 'Circle') {
          setCircleInfo({ radius: layer.getRadius(), centerLoc: layer.getLatLng() });
        }
        const centerLatLng = layer.getBounds ? layer.getBounds().getCenter() : layer.getLatLng();
        const containerPoint = map.latLngToContainerPoint(centerLatLng);
        setDrawingPopoverAnchor({ top: containerPoint.y, left: containerPoint.x });
      });
    };

    map.on('pm:create', (e) => {
      const layer = e.layer;
      const shape = e.shape;

      if (shape === 'Circle') {
        layer.setStyle({ color: '#2196f3', fillColor: '#2196f3', fillOpacity: 0.2 });
      }

      setDrawnLayer({ layer, shape });

      if (shape === 'Line' || shape === 'Polyline') {
        layer.distanceMarkers = [...tempDistanceMarkers];
        layer.on('pm:remove', () => {
          if (layer.distanceMarkers) {
            layer.distanceMarkers.forEach((m) => {
              if (map.hasLayer(m)) map.removeLayer(m);
            });
          }
        });
        tempDistanceMarkers = [];
        return;
      }

      makeNewLayerEditable(layer, shape);

      const centerLatLng = layer.getBounds ? layer.getBounds().getCenter() : layer.getLatLng();
      const containerPoint = map.latLngToContainerPoint(centerLatLng);

      const anchorObj = {
        top: containerPoint.y,
        left: containerPoint.x
      };

      if (shape === 'Polygon') {
        const wkt = getPolygonWKT(layer);
        setPolygonWkt(wkt);
        setDrawingPopoverAnchor(anchorObj);
        setDrawingPopoverOpen(true);
      }

      if (shape === 'Circle') {
        const radius = layer.getRadius(); // meters
        const centerLoc = layer.getLatLng();

        setCirclePopoverAnchor(anchorObj);
        setCircleInfo({ radius, centerLoc });
        setCirclePopoverOpen(true);
      }
    });

    map.on('pm:drawend', ({ shape }) => {
      if (shape === 'Line' || shape === 'Polyline') {
        if (tempDistanceMarkers.length > 0) {
          tempDistanceMarkers.forEach((m) => {
            if (map.hasLayer(m)) map.removeLayer(m);
          });
          tempDistanceMarkers = [];
        }
      }
    });

    markerClusterRef.current = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      // disableClusteringAtZoom: 17,
      iconCreateFunction: function (cluster) {
        return new L.DivIcon({
          html: `<div style="background-color: rgba(72, 93, 154, 0.9); color: white; border-radius: 50%; border: 3px solid white; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-weight: bold; box-shadow: 0 4px 8px rgba(0,0,0,0.3);"><span>${cluster.getChildCount()}</span></div>`,
          className: 'custom-cluster-icon',
          iconSize: new L.Point(40, 40)
        });
      }
    });
    map.addLayer(markerClusterRef.current);

    locationLayerRef.current = L.featureGroup();
    map.addLayer(locationLayerRef.current);

    tripLayerRef.current = L.featureGroup();
    map.addLayer(tripLayerRef.current);

    // map.on('movestart', () => {
    //   setLocPopoverOpen(false);
    // });

    map.on('click', () => {
      map.closePopup();
    });

    mapInstanceRef.current = map;

    setTimeout(() => map.invalidateSize(), 500);

    return () => {
      cancelAnimationFrame(animRef.current);
      map.removeControl(geocoder);
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !map.pm) return;

    if (isTripHistoryOpen) {
      map.pm.addControls({
        drawPolyline: true,
        drawPolygon: true,
        drawCircle: false,
        removalMode: true
      });
    } else {
      map.pm.addControls({
        drawPolyline: true,
        drawPolygon: true,
        drawCircle: true,
        removalMode: true
      });
    }
  }, [isTripHistoryOpen]);

  const vehiclesInCircle = useMemo(() => {
    if (!circlePopoverOpen || !circleInfo.centerLoc) return [];

    const radiusMeters = circleInfo.radius;
    const center = L.latLng(circleInfo.centerLoc.lat, circleInfo.centerLoc.lng);

    return mapData.vehicles
      .filter((v) => {
        const lat = v.lat ?? v.latitude ?? v.centroid_lat ?? v.info?.centroid_lat;
        const lon = v.lon ?? v.lng ?? v.longitude ?? v.centroid_lon ?? v.info?.centroid_lon;
        if (lat == null || lon == null) return false;

        const vLoc = L.latLng(lat, lon);
        return center.distanceTo(vLoc) <= radiusMeters;
      })
      .map((v) => v.vehicle_license_plate || v.info?.name || v.vehicle || 'Unknown');
  }, [circlePopoverOpen, circleInfo, mapData.vehicles]);

  const activeGlowLayerRef = useRef(null);

  const polygonsInCircle = useMemo(() => {
    if (!circlePopoverOpen || !circleInfo.centerLoc || !locationLayerRef.current) return [];

    const radiusMeters = circleInfo.radius;
    const center = L.latLng(circleInfo.centerLoc.lat, circleInfo.centerLoc.lng);
    const result = [];

    locationLayerRef.current.eachLayer((layer) => {
      if (layer instanceof L.Polygon && layer.options && layer.options.data) {
        const latlngs = layer.getLatLngs()[0];
        if (!latlngs || latlngs.length === 0) return;

        const isInside = isPointInPolygon([center.lat, center.lng], layer);

        let vertexInside = false;
        for (let i = 0; i < latlngs.length; i++) {
          if (center.distanceTo(latlngs[i]) <= radiusMeters) {
            vertexInside = true;
            break;
          }
        }

        if (isInside || vertexInside) {
          result.push({
            layer,
            data: layer.options.data
          });
        }
      }
    });

    return result;
  }, [circlePopoverOpen, circleInfo]);

  useEffect(() => {
    if (!circlePopoverOpen && activeGlowLayerRef.current) {
      if (activeGlowLayerRef.current.getElement()) {
        activeGlowLayerRef.current.getElement().classList.remove('polygon-glow-active');
      }
      activeGlowLayerRef.current = null;
    }
  }, [circlePopoverOpen]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const cluster = markerClusterRef.current;
    const locLayer = locationLayerRef.current;

    if (!map || !cluster || !locLayer || isEditingPolygon) return;

    isRecreatingRef.current = true;
    locLayer.clearLayers();

    const { vehicles, locations } = mapData;
    locations.forEach((loc) => {
      if (loc.geom) {
        try {
          const wkt = loc.geom;
          const coordsStr = wkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
          const pairs = coordsStr.split(',');
          const polyCoords = pairs.map((pair) => {
            const [lng, ptLat] = pair.trim().split(' ');
            return [Number(ptLat), Number(lng)];
          });

          if (polyCoords.length > 0) {
            let outlineColor = '#7b1fa2';
            let fillColor = '#7b1fa2';

            if (loc.location_type === 'PIT') {
              outlineColor = '#1565c0';
              fillColor = '#1565c0';
            } else if (loc.location_type === 'Jetty') {
              outlineColor = '#2e7d32';
              fillColor = '#2e7d32';
            } else if (loc.location_type === 'Geofence') {
              const join = loc.join || {};
              outlineColor = join.polygon_type_outline_color || join.polygon_type_fill_color || '#7b1fa2';
              fillColor = join.polygon_type_fill_color || '#7b1fa2';
            }

            const polygon = L.polygon(polyCoords, { color: outlineColor, fillColor: fillColor, weight: 2, fillOpacity: 0.15, data: loc });

            polygon.on('click', (e) => {
              if (isEditingPolygon) return;
              if (map.pm && map.pm.globalRemovalModeEnabled && map.pm.globalRemovalModeEnabled()) return;

              L.DomEvent.stopPropagation(e);
              setActiveLocationPolygon({ layer: polygon, data: loc });

              // const centerLatLng = polygon.getBounds().getCenter();
              // const containerPoint = map.latLngToContainerPoint(centerLatLng);

              // const anchorObj = {
              //   top: containerPoint.y,
              //   left: containerPoint.x
              // };
              // setLocPopoverAnchor(anchorObj);
              setLocPopoverOpen(true);
            });

            locLayer.addLayer(polygon);
          }
        } catch (e) {
          console.warn('WKT Parse error', e);
        }
      } else if (loc.lat != null && loc.lon != null) {
        const marker = L.circleMarker([loc.lat, loc.lon], { radius: 6, color: '#4F90E0', fillOpacity: 0.5 });
        marker.bindPopup(`<b>${loc.name || loc.pool_name}</b><br/>Company: ${loc.join.company_name || '-'}<br/>Type: ${loc.location_type}`);
        locLayer.addLayer(marker);
      }
    });

    cluster.clearLayers();
    vehicleMarkersRef.current = {};

    let styleTag = document.getElementById('glow-blink-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'glow-blink-style';
      styleTag.innerHTML = `
        @keyframes pulseRingRed {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes pulseRingYellow {
          0% { transform: scale(0.8); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .pulse-ring {
          position: absolute;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          z-index: 1;
          display: none;
        }
        .pulse-ring.glow-overspeed {
          display: block;
          background-color: rgba(244, 67, 54, 0.6);
          box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
          animation: pulseRingRed 1s infinite ease-out;
        }
        .pulse-ring.glow-idle {
          display: block;
          background-color: rgba(255, 193, 7, 0.6);
          box-shadow: 0 0 10px rgba(255, 193, 7, 0.8);
          animation: pulseRingYellow 1.5s infinite ease-out;
        }
        .polygon-glow-active {
          stroke: var(--glow-color, #ffffff) !important;
          stroke-width: 4px !important;
          stroke-dasharray: 6 !important;
          animation: polygon-march 1s linear infinite;
          filter: drop-shadow(0px 0px 4px var(--glow-color, #ffffff)) brightness(1.5) !important;
        }
        @keyframes polygon-march {
          to { stroke-dashoffset: -12; }
        }
      `;
      document.head.appendChild(styleTag);
    }

    if (!isTripHistoryOpen) {
      const markers = [];
      let activeRitaseMap = {};
      try {
        const activeRitaseStr = localStorage.getItem('active_ritase_data');
        if (activeRitaseStr) {
          activeRitaseMap = JSON.parse(activeRitaseStr).data || {};
        }
      } catch {}

      const logVehiclesLoadStatus = [];

      vehicles.forEach((vehRaw) => {
        const lat = vehRaw.lat ?? vehRaw.latitude ?? vehRaw.centroid_lat ?? vehRaw.info?.centroid_lat;
        const lon = vehRaw.lon ?? vehRaw.lng ?? vehRaw.longitude ?? vehRaw.centroid_lon ?? vehRaw.info?.centroid_lon;
        if (lat != null && lon != null) {
          const apiAngle = vehRaw.raw?.angle ?? vehRaw.direction ?? vehRaw.info?.direction ?? vehRaw.info?.angle ?? null;
          const rotation = apiAngle != null ? Number(apiAngle) - 90 : 0;
          const iconUrl = getVehicleIcon(vehRaw);
          const iconSrc = typeof iconUrl === 'string' ? iconUrl : iconUrl?.src || iconUrl;

          const speed = Number(vehRaw.raw?.speed || vehRaw.speed || vehRaw.info?.speed || 0);

          const engineStatus = String(vehRaw.engine || vehRaw.engine_status || vehRaw.info?.engine_status || '').toUpperCase();

          const vId = vehRaw.raw?.vehicle_id || vehRaw.vehicle_id || vehRaw.id || vehRaw.vehicle;

          let isInRetase = false;
          let isLoaded = false;
          let ritaseEnd = 0;
          let activeTripInfo = null;

          const rawTime = vehRaw.last_update || vehRaw.info?.modified_date;
          const checkTime = rawTime ? dayjs(rawTime).valueOf() : dayjs().valueOf();

          if (vId && activeRitaseMap[vId] && activeRitaseMap[vId].length > 0) {
            for (const trip of activeRitaseMap[vId]) {
              if (!trip.start) continue;
              const startTime = new Date(trip.start).getTime();
              const pureEndTime = trip.end && trip.end !== 'no-end' ? new Date(trip.end).getTime() : Infinity;
              const GRACE_PERIOD_MS = 30 * 60 * 1000;
              const endGrace = pureEndTime !== Infinity ? pureEndTime + GRACE_PERIOD_MS : Infinity;

              if (checkTime >= startTime && checkTime <= endGrace) {
                isInRetase = true;
                activeTripInfo = trip;
                if (checkTime <= pureEndTime) isLoaded = true;
                if (pureEndTime !== Infinity && pureEndTime > ritaseEnd) ritaseEnd = pureEndTime;
              }
            }
          }

          const trackingStateStr = localStorage.getItem('vehicle_tracking_state');
          let trackingState = {};
          try {
            if (trackingStateStr) trackingState = JSON.parse(trackingStateStr);
          } catch {}

          if (!isLoaded && trackingState[vId]) {
            const vState = trackingState[vId];
            if (vState.checkpoint_status && vState.checkpoint_time >= ritaseEnd) {
              isLoaded = vState.checkpoint_status === 'LOADED';
            }
            if (!isLoaded && vState.history && vState.history.length > 1) {
              const oldest = vState.history[0];
              const newest = vState.history[vState.history.length - 1];
              const deltaDest = oldest.dist_dest - newest.dist_dest;
              const deltaOrig = oldest.dist_orig - newest.dist_orig;
              if (deltaDest > deltaOrig && deltaDest > 0.05) {
                isLoaded = true;
              }
            }
          }

          logVehiclesLoadStatus.push({
            id: vId,
            plate: vehRaw.vehicle_license_plate || vehRaw.info?.name || 'Unknown',
            status: isLoaded ? 'BAWA MUATAN' : 'KOSONG',
            isInRetase,
            speed,
            engine: engineStatus,
            api_trip: activeTripInfo,
            checkpoint_status: trackingState[vId]?.checkpoint_status || null,
            history_length: trackingState[vId]?.history?.length || 0
          });

          const formattedVehForPulse = formatVehicleDataForPopover(vehRaw, notifSettings);
          vehRaw.pulseColor = formattedVehForPulse?.pulseColor;
          vehRaw.pulseReason = formattedVehForPulse?.pulseReason;

          let glowClass = '';
          if (vehRaw.pulseColor === 'error') {
            glowClass = 'glow-overspeed';
          } else if (vehRaw.pulseColor === 'warning') {
            glowClass = 'glow-idle';
          }

          const marker = L.marker([Number(lat), Number(lon)], {
            icon: L.divIcon({
              html: `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative;">
                       <div class="pulse-ring ${glowClass}"></div>
                       <img src="${iconSrc}" style="width:50px; height: 25px; transform-origin: center; transform: rotate(${rotation}deg); transition: transform 0.3s ease-out; z-index: 2; position: relative;" />
                     </div>`,
              className: 'live-car-container',
              iconSize: [50, 25],
              iconAnchor: [16, 16]
            })
          });

          const markerId = getVehicleUniqueId(vehRaw);
          vehicleMarkersRef.current[markerId] = marker;

          const popupContent = document.createElement('div');
          popupContent.id = `popup-vehicle-${markerId}`;
          // Berikan sedikit ukuran awal agar popup Leaflet tidak terlalu kecil
          popupContent.style.minWidth = '275px';
          popupContent.style.minHeight = '300px';

          marker.bindPopup(popupContent, {
            maxWidth: 'auto',
            className: 'custom-react-leaflet-popup' // Tambahkan class ini untuk styling CSS nanti
          });

          marker.on('popupopen', () => {
            // 1. Zoom/Pan ke lokasi
            // Tambahkan yOffset agar posisi marker turun, PopoverVehicle muat utuh
            // Gunakan zoom map saat ini agar tidak memicu perubahan zoom yang membuat cluster unspiderfy/menutup otomatis
            const map = mapInstanceRef.current;
            const currentZ = map ? map.getZoom() : 20;
            flyToWithOffset(Number(lat), Number(lon), currentZ, 180);

            // 2. Set State agar React me-render PopoverVehicle
            setSelectedVehicleId(markerId);
            setSelectedVehicle(formatVehicleDataForPopover(vehRaw, notifSettings));

            // Simpan referensi DOM element tempat PopoverVehicle akan dipasang
            setPopoverAnchorEl(popupContent);
          });

          // Hapus state ketika popup ditutup
          marker.on('popupclose', () => {
            if (!isRecreatingRef.current) {
              if (selectedVehicleIdRef.current === markerId) {
                setSelectedVehicle(null);
                setSelectedVehicleId(null);
                setPopoverAnchorEl(null);
              }
            }
          });

          const plate = vehRaw.vehicle_license_plate || vehRaw.info?.name || 'Unknown';
          marker.bindTooltip(plate, { permanent: true, direction: 'bottom', className: 'vehicle-tooltip', offset: [0, 15] });
          markers.push(marker);
        }
      });

      // console.log('[Map.jsx - Status Muatan Global]', logVehiclesLoadStatus);

      cluster.addLayers(markers);

      // Re-open popup if there was a selected vehicle
      if (selectedVehicleIdRef.current) {
        const activeMarker = vehicleMarkersRef.current[selectedVehicleIdRef.current];
        if (activeMarker) {
          setTimeout(() => {
            if (!activeMarker.isPopupOpen()) {
              activeMarker.openPopup();
            }
          }, 0);
        }
      }
    }
    isRecreatingRef.current = false;
  }, [mapData, isTripHistoryOpen, flyToWithOffset, isEditingPolygon, notifSettings]);

  const handleClosePopover = useCallback(() => {
    // Meminta Leaflet untuk menutup popup aktif (trigger event popupclose)
    const map = mapInstanceRef.current;
    if (map) map.closePopup();
  }, []);

  useEffect(() => {
    if (selectedVehicleId && mapData.vehicles && mapData.vehicles.length > 0) {
      const vehRaw = mapData.vehicles.find((v) => getVehicleUniqueId(v) === selectedVehicleId);
      if (vehRaw) {
        setSelectedVehicle(formatVehicleDataForPopover(vehRaw, notifSettings));
      }
    }
  }, [mapData, notifSettings, selectedVehicleId]);

  const handleViewPolygonFromCircle = (polyObj) => {
    const layer = polyObj.layer;
    if (activeGlowLayerRef.current && activeGlowLayerRef.current.getElement()) {
      activeGlowLayerRef.current.getElement().classList.remove('polygon-glow-active');
      activeGlowLayerRef.current.getElement().style.removeProperty('--glow-color');
    }

    if (activeGlowLayerRef.current === layer) {
      activeGlowLayerRef.current = null;
    } else {
      if (layer.getElement()) {
        const polyColor = layer.options?.color || layer.options?.fillColor || '#ffffff';
        layer.getElement().style.setProperty('--glow-color', polyColor);
        layer.getElement().classList.add('polygon-glow-active');
      }
      activeGlowLayerRef.current = layer;
    }
  };

  const handleEditPolygonFromCircle = (polyObj) => {
    setCirclePopoverOpen(false);
    if (drawnLayer?.layer && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(drawnLayer.layer);
      setDrawnLayer(null);
    }

    setActiveLocationPolygon({ layer: polyObj.layer, data: polyObj.data });

    setTimeout(() => {
      setIsEditingPolygon(true);
      polyObj.layer.pm.enable({
        allowSelfIntersection: false,
        preventMarkerRemoval: false
      });
    }, 100);
  };

  const handleDeletePolygonFromCircle = (polyObj) => {
    setCirclePopoverOpen(false);
    if (drawnLayer?.layer && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(drawnLayer.layer);
      setDrawnLayer(null);
    }
    setActiveLocationPolygon({ layer: polyObj.layer, data: polyObj.data });
    setConfirmOpen(true);
  };

  const handleEditPolygon = () => {
    if (!activeLocationPolygon) return;
    const { layer, data } = activeLocationPolygon;

    setLocPopoverOpen(false);
    setIsEditingPolygon(true);
    setEditedPolygonName(data?.name || data?.pool_name || data?.geofence_name || '');
    setEditedWkt(getPolygonWKT(layer));
    setShowWktInput(false);

    layer.pm.enable({
      allowSelfIntersection: false,
      preventMarkerRemoval: false
    });
  };

  const handleSavePolygonEdit = async () => {
    if (!activeLocationPolygon) return;
    setIsSavingEdit(true);

    const { layer, data: locData } = activeLocationPolygon;
    layer.pm.disable();
    setIsEditingPolygon(false);

    let finalWkt = getPolygonWKT(layer);
    let center = layer.getBounds().getCenter();

    if (showWktInput && editedWkt && editedWkt !== finalWkt) {
      finalWkt = editedWkt;
      try {
        const coordsStr = finalWkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
        const pairs = coordsStr.split(',');
        const polyCoords = pairs.map((pair) => {
          const [lng, lat] = pair.trim().split(/\s+/);
          return [parseFloat(lat), parseFloat(lng)];
        });
        const tempLayer = L.polygon(polyCoords);
        center = tempLayer.getBounds().getCenter();
      } catch (e) {
        console.warn('Failed to parse edited WKT for center', e);
      }
    }

    const payload = { geom: finalWkt, centroid_lat: center.lat, centroid_lon: center.lng, name: editedPolygonName, status: true };

    try {
      if (locData.location_type === 'PIT') {
        await updateGeomPool(locData.id, payload);
      } else if (locData.location_type === 'Jetty') {
        await updateGeomTransitPoint(locData.id, payload);
      } else if (locData.location_type === 'Geofence') {
        await updateGeomGeofence(locData.id, payload);
      }
      notify({
        message: `Polygon ${locData.name || locData.pool_name || locData.geofence_name} updated successfully.`,
        severity: 'success'
      });
    } catch {
      notify({ message: 'Failed update data.', severity: 'error' });
    } finally {
      setIsSavingEdit(false);
      setActiveLocationPolygon(null);
    }
  };

  const handleCancelPolygonEdit = () => {
    if (!activeLocationPolygon) return;
    activeLocationPolygon.layer.pm.disable();
    setIsEditingPolygon(false);
    setActiveLocationPolygon(null);
    setShowWktInput(false);
    notify({ message: 'Polygon editing cancelled.', severity: 'info' });
  };

  const handleDeletePolygonClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
  };
  const handleEditDeleteLocClose = () => {
    setLocPopoverOpen(false);
    setConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!activeLocationPolygon) return;
    setConfirmLoading(true);
    const locData = activeLocationPolygon.data;

    try {
      if (locData.location_type === 'PIT') {
        await deletePool(locData.id);
      } else if (locData.location_type === 'Jetty') {
        await deleteTransitPoint(locData.id);
      } else if (locData.location_type === 'Geofence') {
        await deleteGeofence(locData.id);
      }

      notify({
        message: `Location ${locData.name || locData.pool_name || locData.geofence_name} successfully deleted.`,
        severity: 'success'
      });

      if (mapInstanceRef.current && activeLocationPolygon.layer) {
        mapInstanceRef.current.removeLayer(activeLocationPolygon.layer);
      }
    } catch {
      notify({ message: `Failed to delete ${locData.name || locData.pool_name || locData.geofence_name}.`, severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setLocPopoverOpen(false);
      setActiveLocationPolygon(null);
    }
  };

  const formatDisplayTime = (isoString) => {
    if (!isoString) return '-';
    try {
      let val = isoString;
      if (typeof val === 'string' && /^\d+$/.test(val)) {
        val = parseInt(val, 10);
      }
      const d = new Date(val);
      if (isNaN(d.getTime())) return isoString;

      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');

      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    } catch {
      return isoString;
    }
  };
  const historyClustersRef = useRef({ over: null, idle: null, off: null });
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !tripLayerRef.current) return;

    tripLayerRef.current.clearLayers();
    animIndexRef.current = 0;
    setCurrentIndexState(0);
    updatePlaying(false);

    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }

    if (!isTripHistoryOpen || allWaypoints.length === 0 || derivedPath.length === 0) return;

    const createClusterIcon = (bgColor) => {
      return function (cluster) {
        return new L.DivIcon({
          html: `<div style="background-color: ${bgColor}; color: white; border-radius: 50%; border: 2px solid white; width: 35px; height: 35px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; box-shadow: 0 4px 8px rgba(0,0,0,0.3);"><span>${cluster.getChildCount()}</span></div>`,
          className: 'custom-history-cluster-icon',
          iconSize: new L.Point(35, 35)
        });
      };
    };

    historyClustersRef.current = {
      over: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#F44336')
      }),
      idle: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#FFC107')
      }),
      off: L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        iconCreateFunction: createClusterIcon('#464646')
      })
    };

    let previousValidLatLng = null;

    let lastMarkerType = 'NORMAL';
    let consecutiveMarkerCount = 0;
    const MARKER_SKIP_THRESHOLD = 9;

    const idleMarkerIcon = L.icon({
      iconUrl: EngineIdleMarker,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
    const overMarkerIcon = L.icon({
      iconUrl: EngineOverMarker,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });

    const offMarkerIcon = L.icon({
      iconUrl: EngineOffMarker,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });

    eventMarkersRef.current = {};

    allWaypoints.forEach((wp, index) => {
      // Koordinat diambil dari derivedPath[index] (record yang sama) agar polyline == jalur playback marker
      const currentLatLng = derivedPath[index];

      if (currentLatLng) {
        const engineStatus = String(wp.engine || wp.engine_status || wp.info?.engine_status || '').toUpperCase();
        const currentSpeed = Number(wp.speed || 0);
        const maxSpeedRaw = Number(wp.vehicle?.max_speed || 0);

        const limitSpeed = maxSpeedRaw > 0 ? maxSpeedRaw : 70;

        const spatialId = Number(wp.spatial_id || wp.info?.spatial_id || 0);
        const spatialMaxSpeed = Number(wp.spatial_max_speed || wp.info?.spatial_max_speed || 0);

        const spatialMinSpeed = Number(wp.spatial_min_speed || wp.info?.spatial_min_speed || 0);

        let color = wp.lineColor || '#1d89d5';
        let currentEventType = 'NORMAL';

        if (wp.harsh_event || wp.info?.harsh_event) {
          color = '#F44336';
          currentEventType = 'HARSH_EVENT';
        } else if (wp.is_in_retase !== false && spatialId > 0 && spatialMaxSpeed > 0 && currentSpeed > spatialMaxSpeed) {
          color = '#F39399';
          currentEventType = 'POLYGON_OVERSPEED';
        } else if (spatialId > 0 && spatialMinSpeed > 0 && currentSpeed > 0 && currentSpeed < spatialMinSpeed) {
          color = '#FFC107';
          currentEventType = 'POLYGON_UNDERSPEED';
        } else if (engineStatus === 'OFF') {
          color = '#464646';
          currentEventType = 'OFF';
        } else if (engineStatus === 'IDLE') {
          color = '#FFC107';
          currentEventType = 'IDLE';
        } else if (engineStatus === 'ON') {
          const statusStr = String(wp.status || wp.info?.status_text || wp.type || '').toLowerCase();
          const isStatusOverSpeed = statusStr.includes('over speed') || statusStr.includes('overspeed');
          if (currentSpeed >= limitSpeed || isStatusOverSpeed) {
            color = '#F44336';
            currentEventType = 'OVERSPEED';
          } else {
            color = wp.lineColor || '#1d89d5';
            currentEventType = 'NORMAL';
          }
        }

        if (wp.sensorLabel && wp.sensorLabel !== 'Normal Route') {
          currentEventType = `SENSOR_${wp.sensorLabel}`;
        }

        if (previousValidLatLng) {
          L.polyline([previousValidLatLng, currentLatLng], { color: color, weight: 5, opacity: 0.9 }).addTo(tripLayerRef.current);
        }

        let shouldDrawMarker = false;

        if (currentEventType !== 'NORMAL') {
          if (currentEventType !== lastMarkerType) {
            shouldDrawMarker = true;
            lastMarkerType = currentEventType;
            consecutiveMarkerCount = 1;
          } else {
            consecutiveMarkerCount++;
            if (consecutiveMarkerCount >= MARKER_SKIP_THRESHOLD) {
              shouldDrawMarker = true;
              consecutiveMarkerCount = 1;
            }
          }
        } else {
          lastMarkerType = 'NORMAL';
          consecutiveMarkerCount = 0;
        }

        if (shouldDrawMarker) {
          const rawTime = wp.start_time || wp.start_wp_time || '';
          const displayTime = formatDisplayTime(rawTime);
          let marker = null;

          if (currentEventType === 'HARSH_EVENT') {
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'HARSH_EVENT' }).bindPopup(
              `<b><span style="color: #F44336;">⚠ ${String(wp.harsh_event).toUpperCase().replace('BREAKING', 'BRAKING')}</span></b><br/>` +
                `Speed: <b>${currentSpeed} km/h</b><br/>` +
                `Time: ${displayTime}`
            );
          } else if (currentEventType === 'OFF') {
            marker = L.marker(currentLatLng, { icon: offMarkerIcon, eventType: 'OFF' }).bindPopup(
              `<b>Engine OFF</b><br/>Time: ${displayTime}`
            );
          } else if (currentEventType === 'IDLE') {
            marker = L.marker(currentLatLng, { icon: idleMarkerIcon, eventType: 'IDLE' }).bindPopup(
              `<b>Engine IDLE</b><br/>Time: ${displayTime}`
            );
          } else if (currentEventType === 'POLYGON_OVERSPEED') {
            const sMax = Number(wp.spatial_max_speed || wp.info?.spatial_max_speed || 0);
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'OVERSPEED' }).bindPopup(
              `<b><span style="color: #F39399;">⚠ OVER SPEED IN AREA</span></b><br/>` +
                `Speed: <b>${currentSpeed} km/h</b> (Max: ${sMax} km/h)<br/>` +
                `Time: ${displayTime}`
            );
          } else if (currentEventType === 'POLYGON_UNDERSPEED') {
            const polygonTypeName = wp.polygon_type_name || wp.info?.polygon_type_name || wp.spatial_description || 'Geofence';
            const sMin = Number(wp.spatial_min_speed || wp.info?.spatial_min_speed || 0);
            marker = L.marker(currentLatLng, { icon: idleMarkerIcon, eventType: 'UNDERSPEED' }).bindPopup(
              `<b><span style="color: #FFC107;">⚠ UNDER SPEED (${polygonTypeName})</span></b><br/>` +
                `Speed: <b>${currentSpeed} km/h</b> (Min: ${sMin} km/h)<br/>` +
                `Time: ${displayTime}`
            );
          } else if (currentEventType === 'OVERSPEED') {
            marker = L.marker(currentLatLng, { icon: overMarkerIcon, eventType: 'OVERSPEED' }).bindPopup(
              `<b><span style="color: #F44336;">⚠ OVER SPEED</span></b><br/>` +
                `Speed: <b>${currentSpeed} km/h</b> (Limit: ${limitSpeed} km/h)<br/>` +
                `Time: ${displayTime}`
            );
          } else if (currentEventType.startsWith('SENSOR_')) {
            marker = L.circleMarker(currentLatLng, { radius: 4, color: '#fff', weight: 1, fillColor: color, fillOpacity: 1 }).bindPopup(
              `<b>Sensor: ${wp.sensorLabel}</b><br/>Time: ${displayTime}`
            );
          }

          if (marker) {
            if (['HARSH_EVENT', 'POLYGON_OVERSPEED', 'OVERSPEED'].includes(currentEventType)) {
              historyClustersRef.current.over.addLayer(marker);
            } else if (['IDLE', 'POLYGON_UNDERSPEED'].includes(currentEventType)) {
              historyClustersRef.current.idle.addLayer(marker);
            } else {
              historyClustersRef.current.off.addLayer(marker);
            }
            eventMarkersRef.current[index] = marker;
          }
        }

        previousValidLatLng = currentLatLng;
      }
    });

    tripLayerRef.current.addLayer(historyClustersRef.current.over);
    tripLayerRef.current.addLayer(historyClustersRef.current.idle);
    tripLayerRef.current.addLayer(historyClustersRef.current.off);

    const start = derivedPath[0];
    const end = derivedPath[derivedPath.length - 1];

    startMarkerRef.current = L.circleMarker(start, { radius: 6, color: '#fff', weight: 2, fillColor: 'green', fillOpacity: 1 })
      .addTo(tripLayerRef.current)
      .bindPopup('Start');

    endMarkerRef.current = L.circleMarker(end, { radius: 6, color: '#fff', weight: 2, fillColor: 'red', fillOpacity: 1 })
      .addTo(tripLayerRef.current)
      .bindPopup('End');

    const truckUrl = typeof PurpleCar === 'string' ? PurpleCar : PurpleCar?.src || PurpleCar;

    const movingIcon = L.divIcon({
      html: `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative;">
              <div id="trip-vehicle-pulse" class="pulse-ring"></div>
              <img id="trip-vehicle-img" src="${truckUrl}" style="width: 50px; height: 25px; transform-origin: center; transition: transform 0.3s ease-out; z-index: 2; position: relative;" />
            </div>`,
      className: 'moving-car-container',
      iconSize: [50, 25],
      iconAnchor: [32, 17]
    });

    tripMarkerRef.current = L.marker(start, { icon: movingIcon, zIndexOffset: 2000 }).addTo(tripLayerRef.current);

    const bounds = L.latLngBounds(derivedPath);
    const paddingRight = isSidebarOpen ? 420 : 0;
    map.fitBounds(bounds, { padding: [40, 40], paddingBottomRight: [paddingRight, 200] });
  }, [derivedPath, allWaypoints, isTripHistoryOpen, isSidebarOpen, updatePlaying]);

  const triggerPopup = useCallback((marker, content, isPersistent = false) => {
    if (!marker) return;

    if (!marker._map) return;
    let hasLayer = false;
    let visibleParent = null;

    if (historyClustersRef.current) {
      if (historyClustersRef.current.over?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.over.getVisibleParent(marker);
      } else if (historyClustersRef.current.idle?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.idle.getVisibleParent(marker);
      } else if (historyClustersRef.current.off?.hasLayer(marker)) {
        hasLayer = true;
        visibleParent = historyClustersRef.current.off.getVisibleParent(marker);
      }
    }

    if (hasLayer && visibleParent && visibleParent !== marker) {
      return;
    }

    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
    }

    try {
      if (content) {
        marker.bindPopup(content).openPopup();
      } else {
        if (marker.getPopup && marker.getPopup()) {
          marker.openPopup();
        }
      }

      if (!isPersistent) {
        popupTimeoutRef.current = setTimeout(() => {
          if (marker._map) {
            marker.closePopup();
          }
          popupTimeoutRef.current = null;
        }, 2000);
      }
    } catch (error) {
      console.warn('Marker failed to open popup (likely unmounted):', error);
    }
  }, []);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !tripMarkerRef.current || !isTripHistoryOpen) return;

    let rafId = null;
    let lastTime = performance.now();
    let lastPanTime = performance.now();
    let lastStateUpdate = 0;

    const step = (time) => {
      if (!isPlayingRef.current) return;
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      const currentDerivedPath = derivedPathRef.current;
      const currentDerivedSpeeds = derivedSpeedsRef.current;
      const currentAllWaypoints = allWaypointsRef.current;

      if (!currentDerivedPath || currentDerivedPath.length === 0) return;

      const maxIdx = currentDerivedPath.length - 1;
      const i = Math.floor(animIndexRef.current);
      const nowMs = performance.now();

      if (nowMs - lastStateUpdate > 100) {
        lastStateUpdate = nowMs;

        // derivedPath & allWaypoints sejajar 1:1 -> pakai index yang sama (bukan proporsi antar array)
        const currentData = currentAllWaypoints[i];

        if (currentData) {
          setCurrentPointSpeed((prev) => {
            const newVal = Math.round(currentData.canbus?.canVehicleSpeed || currentData.speed || 0);
            return prev !== newVal ? newVal : prev;
          });

          setCurrentRPM((prev) => {
            const newVal = currentData.canbus?.canEngineRPM || 0;
            return prev !== newVal ? newVal : prev;
          });

          setCurrentFuel((prev) => {
            const newLitre = currentData.can_fuel_litre || 0;
            const newPercent = currentData.can_fuel_percentage || 0;
            if (prev.litre !== newLitre || prev.percent !== newPercent) {
              return { litre: newLitre, percent: newPercent, cap: currentData.fuel_tank || 0 };
            }
            return prev;
          });
        }

        setCurrentIndexState((prev) => (prev !== i ? i : prev));
      }

      if (i !== lastIndexRef.current) {
        const previousIndex = lastIndexRef.current;
        lastIndexRef.current = i;

        const mappedWpIndex = i;
        const currentData = currentAllWaypoints[mappedWpIndex];
        const currentLoc = currentDerivedPath[i];

        if (currentData && currentLoc) {
          if (i === 0 && startMarkerRef.current) {
            triggerPopup(startMarkerRef.current, null, true);
          } else if (i === maxIdx && endMarkerRef.current) {
            triggerPopup(endMarkerRef.current, null, true);
          } else {
            if (previousIndex === 0 && startMarkerRef.current) {
              triggerPopup(startMarkerRef.current, null, false);
            } else if (previousIndex === maxIdx && endMarkerRef.current) {
              triggerPopup(endMarkerRef.current, null, false);
            }

            const eventMarker = eventMarkersRef.current[mappedWpIndex];
            if (eventMarker) {
              if (nowMs - lastEventPopupTimeRef.current > 2000) {
                lastEventPopupTimeRef.current = nowMs;
                const popupContent = eventMarker.getPopup() ? eventMarker.getPopup().getContent() : null;
                if (popupContent) {
                  triggerPopup(tripMarkerRef.current, popupContent);
                } else {
                  triggerPopup(eventMarker);
                }
              }
            }
          }

          if (locationLayerRef.current) {
            let foundArea = null;

            locationLayerRef.current.eachLayer((layer) => {
              if (layer instanceof L.Polygon && isPointInPolygon(currentLoc, layer)) {
                foundArea = layer.options.data;
              }
            });

            if (foundArea) {
              const areaId = foundArea.id || foundArea.pool_name;

              if (currentAreaIdRef.current !== areaId) {
                currentAreaIdRef.current = areaId;
                const name = foundArea.name || foundArea.pool_name || foundArea.geofence_name || 'Area';
                const type = foundArea.location_type || 'Location';
                const timeStr = formatDisplayTime(currentData.start_time || currentData.start_wp_time);
                triggerPopup(tripMarkerRef.current, `<b>Vehicle enter ${name} (${type})</b><br/>at: ${timeStr}`);
              }
            } else {
              currentAreaIdRef.current = null;
            }
          }
        }
      }

      const simSpeedVal = 36;
      const speedAtIndex = currentDerivedSpeeds[i] ?? simSpeedVal;

      // Smart Speed Detection:
      // Kecepatan gerak cursor disesuaikan langsung dengan kecepatan kendaraan yang terekam.
      // Diberikan minimum threshold (misal 5) agar saat Idle/Off (speed 0), kursor tetap berjalan mulus
      // menyeberangi gap waktu (sehingga progress bar dan Gantt crosshair tetap jalan).
      const baseSpeed = Math.max(5, Number(speedAtIndex));
      const delta = (baseSpeed / 20) * dt * playbackSpeedRef.current;
      let nextIndex = animIndexRef.current + delta;

      if (nextIndex < 0) {
        nextIndex = 0;
        updatePlaying(false);
      } else if (nextIndex > maxIdx) {
        nextIndex = maxIdx;
        updatePlaying(false);
      }

      if (animIndexRef.current >= maxIdx && delta > 0) {
        nextIndex = 0;
        updatePlaying(true);
      }
      if (animIndexRef.current <= 0 && delta < 0) {
        nextIndex = maxIdx;
        updatePlaying(true);
      }

      animIndexRef.current = nextIndex;

      // Linear Interpolation untuk Smooth Playback (mengikuti garis secara presisi)
      const floorIdx = Math.floor(animIndexRef.current);
      const ceilIdx = Math.min(maxIdx, Math.ceil(animIndexRef.current));
      const fraction = animIndexRef.current - floorIdx;

      const locA = currentDerivedPath[floorIdx];
      const locB = currentDerivedPath[ceilIdx];

      let newLoc = locA;
      if (locA && locB && floorIdx !== ceilIdx) {
        newLoc = [locA[0] + (locB[0] - locA[0]) * fraction, locA[1] + (locB[1] - locA[1]) * fraction];
      }

      if (newLoc && tripMarkerRef.current) {
        tripMarkerRef.current.setLatLng(newLoc);

        const mappedWpIndexForAngle = Math.min(maxIdx, Math.round(animIndexRef.current));
        const currentDataWp = currentAllWaypoints[mappedWpIndexForAngle];
        const apiAngle = currentDataWp?.angle ?? currentDataWp?.direction ?? null;

        updateVehicleRotation(animIndexRef.current, currentDerivedPath, apiAngle, currentDataWp);
        // ---------------------------------------------------------------------------------

        if (nowMs - lastPanTime > 30) {
          lastPanTime = nowMs;
          panToWithOffset(newLoc[0], newLoc[1], false);
        }
      }

      if (isPlayingRef.current) {
        rafId = requestAnimationFrame(step);
      }
    };

    if (isPlaying) {
      lastTime = performance.now();
      rafId = requestAnimationFrame(step);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isPlaying, isTripHistoryOpen, panToWithOffset, triggerPopup, updatePlaying, updateVehicleRotation]);

  const handlePlayPause = useCallback(() => updatePlaying(!isPlayingRef.current), [updatePlaying]);
  const seekToIndex = useCallback(
    (index) => {
      if (!derivedPath.length) return;
      const i = Math.min(Math.max(0, Math.floor(index)), derivedPath.length - 1);
      animIndexRef.current = i;
      setCurrentIndexState(i);

      if (tripMarkerRef.current) {
        const p = derivedPath[i];

        tripMarkerRef.current.setLatLng(p);
        flyToWithOffset(p[0], p[1]);

        // derivedPath & allWaypoints sejajar 1:1
        const currentDataWp = allWaypointsRef.current[i];
        const apiAngle = currentDataWp?.angle ?? currentDataWp?.direction ?? null;
        if (currentDataWp) {
          setCurrentPointSpeed(Math.round(currentDataWp.canbus?.canVehicleSpeed || currentDataWp.speed || 0));
          setCurrentRPM(currentDataWp.canbus?.canEngineRPM || 0);
          setCurrentFuel({
            litre: currentDataWp.can_fuel_litre || 0,
            percent: currentDataWp.can_fuel_percentage || 0,
            cap: currentDataWp.fuel_tank || 0
          });
        }

        updateVehicleRotation(i, derivedPath, apiAngle, currentDataWp);
        // -------------------------------------------------------------------------
      }
    },
    [derivedPath, flyToWithOffset, updateVehicleRotation]
  );

  const handleSeekToPercent = useCallback(
    (pct) => {
      if (!allWaypoints || allWaypoints.length === 0) return;

      const maxIndex = allWaypoints.length - 1;
      const targetIndex = Math.round((pct / 100) * maxIndex);
      seekToIndex(targetIndex);
    },
    [allWaypoints, seekToIndex]
  );

  const handleNext = useCallback(() => {
    seekToIndex(Math.min(maxIndex, animIndexRef.current + 1));
    updatePlaying(false);
  }, [maxIndex, seekToIndex, updatePlaying]);

  const handlePrev = useCallback(() => {
    seekToIndex(Math.max(0, animIndexRef.current - 1));
    updatePlaying(false);
  }, [seekToIndex, updatePlaying]);

  const handleSelectVehicleFromSidebar = useCallback(
    (mappedVehicleObj) => {
      if (!mappedVehicleObj || !mapInstanceRef.current) {
        setSelectedVehicle(null);
        setPopoverAnchorEl(null);
        return;
      }

      const v = mappedVehicleObj.raw || mappedVehicleObj;
      const lat = v.lat ?? v.latitude ?? v.centroid_lat ?? v.info?.centroid_lat;
      const lon = v.lon ?? v.lng ?? v.longitude ?? v.centroid_lon ?? v.info?.centroid_lon;

      if (lat != null && lon != null) {
        const vehicleId = getVehicleUniqueId(v);
        const marker = vehicleMarkersRef.current[vehicleId];
        if (marker) {
          if (markerClusterRef.current) {
            markerClusterRef.current.zoomToShowLayer(marker, () => {
              marker.openPopup();
            });
          } else {
            flyToWithOffset(Number(lat), Number(lon), 21);
            marker.openPopup();
          }
        } else {
          flyToWithOffset(Number(lat), Number(lon), 21);
        }
      }
    },
    [flyToWithOffset]
  );

  const handleSelectLocation = useCallback(
    (location) => {
      if (!location) return;

      const locData = location.raw || location;
      let lat = locData.lat ?? locData.centroid_lat;
      let lon = locData.lon ?? locData.lng ?? locData.centroid_lon;

      if (lat != null && lon != null) {
        flyToWithOffset(Number(lat), Number(lon), 16);

        setTimeout(() => {
          const map = mapInstanceRef.current;
          if (!map || !locationLayerRef.current) return;

          let targetLayer = null;
          locationLayerRef.current.eachLayer((layer) => {
            const layerData = layer.options?.data;
            const locId = location.id || location.raw?.id;

            if (layerData && layerData.id === locId) {
              targetLayer = layer;
            }
          });

          if (targetLayer) {
            setActiveLocationPolygon({ layer: targetLayer, data: targetLayer.options.data });
            // const centerLatLng = targetLayer.getBounds ? targetLayer.getBounds().getCenter() : targetLayer.getLatLng();
            // const containerPoint = map.latLngToContainerPoint(centerLatLng);

            // setLocPopoverAnchor({ top: containerPoint.y, left: containerPoint.x });
            setLocPopoverOpen(true);
          }
        }, 1500);
      }
    },
    [flyToWithOffset]
  );

  const handleSetMapCenter = useCallback(
    (coords) => {
      if (coords && coords.lat != null && coords.lon != null) flyToWithOffset(Number(coords.lat), Number(coords.lon), 20);
    },
    [flyToWithOffset]
  );

  const handleDataLoaded = useCallback((data) => {
    setMapData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) {
        return prev;
      }
      return data;
    });
  }, []);

  const handleOpenTripHistory = useCallback(
    (id, veh) => {
      handleClosePopover();
      setTripVehicleTarget(veh);
      setIsTripHistoryOpen(true);
      setIsSidebarOpen(true);
    },
    [handleClosePopover]
  );
  const handleOpenVideo = useCallback(
    (vehicleData) => {
      if (
        !vehicleData?.raw?.raw?.integration_imei ||
        vehicleData?.raw?.raw?.integration_imei === '-' ||
        vehicleData?.raw?.raw?.integration_imei === 'Camera not installed'
      ) {
        notify({ message: 'There are no cameras installed on this vehicle.', severity: 'warning' });
        return;
      }
      setVideoTarget(vehicleData);
    },
    [notify]
  );

  const handleCloseTripHistory = useCallback(() => {
    setIsTripHistoryOpen(false);
    setTripVehicleTarget(null);
    setTripWaypoints({ all: [], normal: [], off: [], drb: [] });
    if (tripLayerRef.current) tripLayerRef.current.clearLayers();
  }, []);

  const handleWaypointsLoaded = useCallback((waypointsData) => {
    const safeData = waypointsData || { all: [], normal: [], off: [], drb: [] };
    setTripWaypoints((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(safeData)) {
        return prev;
      }
      return safeData;
    });
  }, []);

  const handleOpenMasterForm = (type) => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen();
    }

    if (type === 'pit') setPitDialogOpen(true);
    else if (type === 'jetty') setJettyDialogOpen(true);
    else if (type === 'geofence') setGeoDialogOpen(true);
  };

  const [activeBaseMap, setActiveBaseMap] = useState('Satelite Map');

  const handleAddEditSuccess = (msg) => {
    // Form component will handle the notification
    setDrawingPopoverOpen(false);
    if (drawnLayer?.layer && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(drawnLayer.layer);
    }
  };

  const handleBaseMapChange = (mapName) => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    Object.values(baseMaps).forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    if (baseMaps[mapName]) {
      baseMaps[mapName].addTo(map);
    }
    setActiveBaseMap(mapName);

    setIsLayerMenuOpen(false);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .vehicle-tooltip { background-color: rgba(255,255,255,0.9); border: 1px solid #ccc; border-radius: 4px; font-weight: bold; font-size: 11px; color: #333; padding: 2px 4px; }
        .leaflet-tooltip-bottom:before { border-bottom-color: rgba(255,255,255,0.9); }
        .custom-cluster-icon { background: transparent; border: none; }
        .moving-car-icon { transition: transform 0.1s linear; }
        .leaflet-pm-toolbar .leaflet-buttons-control-button {
            color: #485D9A !important;
        }
        .leaflet-pm-toolbar .leaflet-buttons-control-button .control-icon {
            fill: #485D9A !important;
            stroke: #485D9A !important;
        }
        .leaflet-pm-toolbar .active .control-icon {
            fill: white !important; 
            stroke: white !important;
        }
        .leaflet-pm-toolbar .active {
            background-color: #485D9A !important;
        }
        .leaflet-top.leaflet-left .leaflet-control { margin-top: 10px; margin-left: 10px; }
        .leaflet-top.leaflet-left .leaflet-control { margin-top: 10px; margin-left: 10px; }

        /* Fullscreen Container Styles */
        .fullscreen-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          background: #f4f4f4;
        }
        :fullscreen .MuiDialog-root, 
        :-webkit-full-screen .MuiDialog-root, 
        :-moz-full-screen .MuiDialog-root {
          position: fixed !important;
          z-index: 10000 !important;
        }
        .leaflet-control-layers-toggle {
          display: none !important;
        }

        /* Mengatur form list layer bawaan leaflet agar melayang (dropdown) */
        .leaflet-control-layers-list {
          display: none; /* Default tertutup */
        }

        /* Kelas khusus ketika state isLayerMenuOpen = true */
        .custom-layer-open .leaflet-control-layers-list {
          display: block;
          position: absolute;
          top: 35px; /* Tepat di bawah IconButton */
          right: 35px; /* Menyesuaikan agar pas di bawah icon */
          background: white;
          padding: 10px;
          border-radius: 4px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          z-index: 10000;
          min-width: 150px;
        }
        
        /* Merapikan jarak antar radio button */
        .leaflet-control-layers-base label {
            margin-bottom: 5px;
            cursor: pointer;
            font-size: 13px;
        }
        .custom-measure-tooltip {
          background: transparent;
          border: none;
          width: auto !important; /* Memaksa width mengikuti konten */
          height: auto !important; /* Memaksa height mengikuti konten */
          overflow: visible !important; /* Mencegah pemotongan visual */
          margin-left: 10px !important; /* Geser marker */
          margin-top: -12px !important;
        }
          .custom-react-leaflet-popup .leaflet-popup-content-wrapper {
          padding: 0 !important;
          border-radius: 12px !important;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.15) !important;
        }
        .custom-react-leaflet-popup .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }
        .custom-react-leaflet-popup .leaflet-popup-tip-container {
          /* Opsional: sesuaikan panah bawah popup */
        }
      `}</style>

      <Box
        ref={wrapperRef}
        className={isFullscreen ? 'fullscreen-wrapper' : ''}
        sx={
          !isFullscreen
            ? {
                width: '100%',
                height: '100%',
                flex: 1,
                minHeight: 0,
                position: 'relative',
                display: 'flex',
                overflow: 'hidden',
                bgcolor: '#f4f4f4'
              }
            : {}
        }
      >
        <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%', zIndex: 1 }} />

        <Box
          sx={{
            position: 'absolute',
            top: 14,
            right: isSidebarOpen ? 345 : 50,
            zIndex: 1000,
            gap: 0.5
          }}
        >
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 1,
              boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
              p: 0.5
            }}
          >
            <IconButton
              sx={{ width: 28, height: 28 }}
              onClick={() => {
                setIsLegendOpen(!isLegendOpen);
                setIsLayerMenuOpen(false);
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              sx={{ width: 28, height: 28 }}
              onClick={() => {
                setIsLayerMenuOpen(!isLayerMenuOpen);
                setIsLegendOpen(false);
              }}
            >
              <LayersIcon />
            </IconButton>
            <IconButton sx={{ width: 28, height: 28 }} onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>

          {isLayerMenuOpen && (
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 1,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                p: 1,
                width: 150
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, px: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                  {t('mapsTracking.mapLayer', 'Map Layer')}
                </Typography>
                <IconButton size="small" onClick={() => setIsLayerMenuOpen(false)} sx={{ p: 0.2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 0.5 }} />
              <List disablePadding>
                {Object.keys(baseMaps).map((mapName) => {
                  const isActive = mapName === activeBaseMap;

                  return (
                    <ListItem
                      key={mapName}
                      button
                      onClick={() => handleBaseMapChange(mapName)}
                      sx={{
                        p: 0.5,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: isActive ? 'rgba(72, 93, 154, 0.15)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.25)' },
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <ListItemText
                        primary={mapName}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',

                          color: isActive ? '#485D9A' : '#333',
                          fontWeight: isActive ? 'bold' : 'normal'
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
          {isLegendOpen && (
            <Box
              sx={{
                width: 300,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                p: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                transition: 'left 0.3s ease'
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    pb: 0.5
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: '#2c3e50', fontSize: '0.75rem', textTransform: 'uppercase' }}
                  >
                    {t('mapsTracking.vehicleLegend', 'Vehicle Legend')}
                  </Typography>
                  <IconButton size="small" onClick={() => setIsLegendOpen(false)} sx={{ p: 0.2 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid spacing={2}>
                  <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                    <img src={GreenCar} alt="Moving" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                      Moving
                    </Typography>
                  </Grid>
                  <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                    <img src={YellowCar} alt="Idle" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                      Idle
                    </Typography>
                  </Grid>
                  <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                    <img src={RedCar} alt="Delay" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                      Delay
                    </Typography>
                  </Grid>
                  <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                    <img src={PurpleCar} alt="Parked" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                      Parked
                    </Typography>
                  </Grid>
                  <Grid container spacing={2} justifyContent="start" alignItems="center" size={{ xs: 12, md: 6 }}>
                    <img src={BlackCar} alt="Stopped" style={{ width: 50, height: 25, objectFit: 'contain' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                      Off / Stopped
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    mb: 1,
                    color: '#2c3e50',
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    pb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {t('mapsTracking.warningPulse', 'Peringatan (Pulse)')}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: '8px 12px', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: '2px solid rgba(243, 147, 153, 0.8)',
                      bgcolor: 'rgba(243, 147, 153, 0.3)',
                      justifySelf: 'center'
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                    {t('mapsTracking.pinkPulse', 'Pink (Over Speed in Area)')}
                  </Typography>

                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: '2px solid rgba(244, 67, 54, 0.8)',
                      bgcolor: 'rgba(244, 67, 54, 0.3)',
                      justifySelf: 'center'
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                    {t('mapsTracking.redPulse', 'Merah (Over Speed Biasa / Harsh Event)')}
                  </Typography>

                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: '2px solid rgba(255, 193, 7, 0.8)',
                      bgcolor: 'rgba(255, 193, 7, 0.3)',
                      justifySelf: 'center'
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
                    {t('mapsTracking.yellowPulse', 'Kuning (Underspeed / Idle)')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ position: 'absolute', top: 0, right: 0, height: '100%', zIndex: 999 }}>
          <MapSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)}>
            {!isTripHistoryOpen ? (
              <MapContent
                onSelectVehicleFromSidebar={handleSelectVehicleFromSidebar}
                onSetMapCenter={handleSetMapCenter}
                onDataLoaded={handleDataLoaded}
                onSelectLocation={handleSelectLocation}
                followedVehicleId={followedVehicleId}
                onFollowVehicle={handleFollowVehicle}
              />
            ) : (
              <TripHistoryList
                vehicle={tripVehicleTarget}
                onBack={handleCloseTripHistory}
                onWaypointsLoaded={handleWaypointsLoaded}
                isFullscreen={isFullscreen}
                onLoadingStateChange={(loading, progress) => {
                  setIsWpLoading(loading);
                  setWpLoadingProgress(progress);
                }}
              />
            )}
          </MapSidebar>
        </Box>

        {isTripHistoryOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: isSidebarOpen ? 'calc(100% - 300px)' : '100%',
              zIndex: 998,
              transition: 'width 0.3s ease-in-out'
            }}
          >
            <BottomPopoverTripHistory
              open={isTripHistoryOpen}
              onClose={handleCloseTripHistory}
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              onNext={handleNext}
              onPrev={handlePrev}
              onSeekToPercent={handleSeekToPercent}
              allWaypoints={allWaypoints}
              onSeekToIndex={(index) => {
                seekToIndex(index);
              }}
              currentIndexState={Math.round((currentIndexState / maxIndex) * (allWaypoints.length - 1))}
              currentPercent={currentPercent}
              currentPointSpeed={currentPointSpeed}
              currentRPM={currentRPM}
              currentFuel={currentFuel}
              currentProgressColor={allWaypoints[currentIndexState]?.lineColor || '#1d89d5'}
              totalDurationSec={0}
              playbackSpeed={playbackSpeed}
              onChangeSpeed={handleChangePlaybackSpeed}
              tripVehicleTarget={tripVehicleTarget}
              searchStart={tripWaypoints?.searchStart}
              searchEnd={tripWaypoints?.searchEnd}
              isLoading={isWpLoading}
              loadingProgress={wpLoadingProgress}
            />
          </Box>
        )}
        {isEditingPolygon && activeLocationPolygon && (
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: isSidebarOpen ? '40%' : '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 350
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight="bold" color="#485D9A">
                  {t('mapsTracking.editArea', 'Edit Area:')}
                </Typography>
                <TextField
                  size="small"
                  variant="outlined"
                  value={editedPolygonName}
                  onChange={(e) => setEditedPolygonName(e.target.value.toUpperCase())}
                  sx={{ minWidth: 150, '& .MuiInputBase-input': { p: '4px 8px', fontSize: '0.875rem' } }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleSavePolygonEdit}
                  disabled={isSavingEdit}
                  sx={{ textTransform: 'none', bgcolor: '#485D9A' }}
                >
                  {t('mapsTracking.saveBtn', 'Simpan')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelPolygonEdit}
                  disabled={isSavingEdit}
                  sx={{ textTransform: 'none' }}
                >
                  {t('mapsTracking.cancelBtn', 'Cancel')}
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ width: '100%', mt: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                {showWktInput && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      py: 0,
                      px: 0.5,
                      minWidth: 'auto',
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                      color: '#0079fa'
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '1rem' }} /> Please enter your WKT geometry data here.
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => {
                    if (!showWktInput && activeLocationPolygon) {
                      setEditedWkt(getPolygonWKT(activeLocationPolygon.layer));
                    }
                    setShowWktInput(!showWktInput);
                  }}
                  sx={{ fontSize: '0.75rem', py: 0, px: 1, minWidth: 'auto', bgcolor: '#ebf5ff', color: '#0079fa' }}
                >
                  {showWktInput ? 'Cancel' : 'Edit Draw Location (WKT)'}
                </Button>
              </Box>
              {showWktInput && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  size="small"
                  value={editedWkt}
                  onChange={(e) => setEditedWkt(e.target.value.toUpperCase().replace(/[^A-Z0-9\s().,-]/g, ''))}
                  sx={{ mt: 1, '& .MuiInputBase-input': { fontSize: '0.75rem', fontFamily: 'monospace' } }}
                />
              )}
            </Box>
          </Box>
        )}
        {drawingPopoverOpen && drawingPopoverAnchor && (
          <Box
            sx={{
              position: 'absolute',
              top: 24,
              left: isSidebarOpen ? '40%' : '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.5)',
              p: 2,
              minWidth: 450,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="#1e293b">
                {t('mapsTracking.savePolygonTo', 'Simpan Polygon ke ?')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setDrawingPopoverOpen(false);
                  if (drawnLayer?.layer && mapInstanceRef.current) {
                    mapInstanceRef.current.removeLayer(drawnLayer.layer);
                  }
                }}
                sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9', color: '#ef4444' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button
                variant="contained"
                size="small"
                onClick={() => handleOpenMasterForm('pit')}
                sx={{
                  flex: 1,
                  bgcolor: '#f8fafc',
                  color: '#1565c0',
                  boxShadow: 'none',
                  border: '1px solid #cbd5e1',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#1458a5', color: '#fff', border: '1px solid #1458a5', boxShadow: 'none' }
                }}
              >
                {t('mapsTracking.masterPit', 'Master Pit')}
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={() => handleOpenMasterForm('jetty')}
                sx={{
                  flex: 1,
                  bgcolor: '#f8fafc',
                  color: '#2e7d32',
                  boxShadow: 'none',
                  border: '1px solid #cbd5e1',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#2e7d32', color: '#fff', border: '1px solid #2e7d32', boxShadow: 'none' }
                }}
              >
                {t('mapsTracking.masterJetty', 'Master Jetty')}
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={() => handleOpenMasterForm('geofence')}
                sx={{
                  flex: 1,
                  bgcolor: '#f8fafc',
                  color: '#7b1fa2',
                  boxShadow: 'none',
                  border: '1px solid #cbd5e1',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#7b1fa2', color: '#fff', border: '1px solid #7b1fa2', boxShadow: 'none' }
                }}
              >
                {t('mapsTracking.geofence', 'Geofence')}
              </Button>
            </Stack>
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<DeleteOutlineIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center' }}
              onClick={() => {
                setDrawingPopoverOpen(false);
                if (drawnLayer?.layer && mapInstanceRef.current) {
                  mapInstanceRef.current.removeLayer(drawnLayer.layer);
                }
              }}
            >
              {t('mapsTracking.deletePolygonCancel', 'Delete Polygon & Cancel')}
            </Button>
          </Box>
        )}
        {locPopoverOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 24,
              left: isSidebarOpen ? '40%' : '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.5)',
              p: 2,
              minWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              pt: 3
            }}
          >
            <IconButton size="small" onClick={handleEditDeleteLocClose} sx={{ position: 'absolute', top: 4, right: 4 }}>
              <CloseIcon fontSize="small" />
            </IconButton>

            <Typography variant="subtitle1" fontWeight="bold" color="#485D9A" pr={2}>
              {activeLocationPolygon?.data?.name || activeLocationPolygon?.data?.pool_name || activeLocationPolygon?.data?.geofence_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('mapsTracking.company', 'Company:')}{' '}
              {activeLocationPolygon?.data?.join?.company_name || activeLocationPolygon?.data?.company?.name || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              {t('mapsTracking.type', 'Tipe:')} {activeLocationPolygon?.data?.location_type}
            </Typography>
            {(activeLocationPolygon?.data?.max_speed > 0 || activeLocationPolygon?.data?.min_speed > 0) && (
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                {activeLocationPolygon?.data?.max_speed > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title="Max Speed">
                      <SpeedIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </Tooltip>
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      {activeLocationPolygon.data.max_speed} Km/H
                    </Typography>
                  </Stack>
                )}
                {activeLocationPolygon?.data?.min_speed > 0 && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title="Min Speed">
                      <SpeedIcon fontSize="small" sx={{ color: 'info.main' }} />
                    </Tooltip>
                    <Typography variant="caption" color="info.main" fontWeight="bold">
                      {activeLocationPolygon.data.min_speed} Km/H
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEditPolygon}
                sx={{ bgcolor: '#485D9A', textTransform: 'none' }}
              >
                {t('mapsTracking.editShape', 'Edit Polygon')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleDeletePolygonClick}
                sx={{ textTransform: 'none' }}
              >
                {t('mapsTracking.deleteBtn', 'Hapus')}
              </Button>
            </Stack>
          </Box>
        )}
        {circlePopoverOpen && circlePopoverAnchor && (
          <Box
            sx={{
              position: 'absolute',
              top: circlePopoverAnchor.top + 20,
              left: circlePopoverAnchor.left,
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              p: 2,
              minWidth: 250,
              maxHeight: 300,
              overflowY: 'auto',
              pt: 4
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                setCirclePopoverOpen(false);
                if (drawnLayer?.layer && mapInstanceRef.current) {
                  mapInstanceRef.current.removeLayer(drawnLayer.layer);
                }
              }}
              sx={{ position: 'absolute', top: 4, right: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Typography variant="h5" fontWeight="bold" color="#485D9A">
              {t('mapsTracking.areaInfo', 'Informasi Area')}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              <b>{t('mapsTracking.radius', 'Radius:')}</b> {(circleInfo.radius / 1000).toFixed(2)} km
            </Typography>
            <Typography variant="body2" mb={1}>
              <b>{t('mapsTracking.totalVehicles', 'Total Vehicle:')}</b> {vehiclesInCircle.length} unit
            </Typography>
            {vehiclesInCircle.length > 0 && (
              <List dense disablePadding>
                {vehiclesInCircle.map((plate, i) => (
                  <ListItem key={i} disablePadding>
                    <ListItemText primary={plate} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </ListItem>
                ))}
              </List>
            )}

            <Typography variant="body2" mt={1} mb={1}>
              <b>{t('mapsTracking.totalArea', 'Total Area:')}</b> {polygonsInCircle.length} unit
            </Typography>
            {polygonsInCircle.length > 0 && (
              <List dense disablePadding>
                {polygonsInCircle.map((polyObj, i) => {
                  const polyName = polyObj.data?.name || polyObj.data?.pool_name || polyObj.data?.geofence_name || 'Unknown Area';
                  return (
                    <ListItem key={i} disablePadding sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <ListItemText
                        primary={polyName}
                        primaryTypographyProps={{ fontSize: '0.8rem', noWrap: true, width: '130px' }}
                        title={polyName}
                      />
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Area" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleViewPolygonFromCircle(polyObj)}
                            sx={{
                              p: 0.5,
                              color: activeGlowLayerRef.current === polyObj.layer ? '#ed6c02 !important' : '#485D9A !important',
                              transition: 'all 0.2s ease',
                              '&:hover': { transform: 'scale(1.2)' },
                              '& svg': {
                                fill: activeGlowLayerRef.current === polyObj.layer ? '#ed6c02 !important' : '#485D9A !important',
                                color: activeGlowLayerRef.current === polyObj.layer ? '#ed6c02 !important' : '#485D9A !important'
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Area" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleEditPolygonFromCircle(polyObj)}
                            sx={{
                              p: 0.5,
                              color: '#0288d1 !important',
                              transition: 'all 0.2s ease',
                              '&:hover': { transform: 'scale(1.2)' },
                              '& svg': {
                                fill: '#0288d1 !important',
                                color: '#0288d1 !important'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Area" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePolygonFromCircle(polyObj)}
                            sx={{
                              p: 0.5,
                              color: '#d32f2f !important',
                              transition: 'all 0.2s ease',
                              '&:hover': { transform: 'scale(1.2)' },
                              '& svg': {
                                fill: '#d32f2f !important',
                                color: '#d32f2f !important'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}
            <Button
              fullWidth
              variant="contained"
              color="error"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => {
                setCirclePopoverOpen(false);
                if (drawnLayer?.layer && mapInstanceRef.current) {
                  mapInstanceRef.current.removeLayer(drawnLayer.layer);
                }
              }}
            >
              {t('mapsTracking.closeDeleteCircle', 'Tutup & Hapus Circle')}
            </Button>
          </Box>
        )}

        <PopoverVehicle
          open={Boolean(selectedVehicle)}
          anchorEl={popoverAnchorEl}
          selected={selectedVehicle}
          onClose={handleClosePopover}
          onTripHistory={handleOpenTripHistory}
          onOpenVideo={handleOpenVideo}
          mapInstance={mapInstanceRef.current}
        />
        <VideoStreaming open={Boolean(videoTarget)} onClose={() => setVideoTarget(null)} vehicle={videoTarget} />
        {confirmOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: isSidebarOpen ? '40%' : '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="#485D9A">
              {t('mapsTracking.deleteArea', 'Hapus Area:')} {activeLocationPolygon?.data?.name || activeLocationPolygon?.data?.pool_name}?
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleConfirmDelete}
                disabled={confirmLoading}
                sx={{ textTransform: 'none', bgcolor: '#485D9A' }}
              >
                {confirmLoading ? t('mapsTracking.deleting', 'Deleting...') : t('mapsTracking.deleteBtn', 'Delete')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleConfirmClose}
                disabled={confirmLoading}
                sx={{ textTransform: 'none' }}
              >
                {t('mapsTracking.cancelBtn', 'Cancel')}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      {pitDialogOpen && (
        <MasterPitAddEdit
          open={pitDialogOpen}
          onClose={() => {
            setPitDialogOpen(false);
            if (drawnLayer?.layer && mapInstanceRef.current) {
              const bounds = drawnLayer.layer.getBounds ? drawnLayer.layer.getBounds() : null;
              if (bounds) mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
            }
          }}
          onSuccess={handleAddEditSuccess}
          passedWkt={polygonWkt}
          disabledViewMap={true}
        />
      )}
      {jettyDialogOpen && (
        <MasterJettyAddEdit
          open={jettyDialogOpen}
          onClose={() => {
            setJettyDialogOpen(false);
            if (drawnLayer?.layer && mapInstanceRef.current) {
              const bounds = drawnLayer.layer.getBounds ? drawnLayer.layer.getBounds() : null;
              if (bounds) mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
            }
          }}
          onSuccess={handleAddEditSuccess}
          passedWkt={polygonWkt}
          disabledViewMap={true}
        />
      )}
      {geoDialogOpen && (
        <MasterGeofenceLibraryAddEdit
          open={geoDialogOpen}
          onClose={() => {
            setGeoDialogOpen(false);
            if (drawnLayer?.layer && mapInstanceRef.current) {
              const bounds = drawnLayer.layer.getBounds ? drawnLayer.layer.getBounds() : null;
              if (bounds) mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
            }
          }}
          onSuccess={handleAddEditSuccess}
          passedWkt={polygonWkt}
          disabledViewMap={true}
        />
      )}
    </Box>
  );
}
